import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "react-query";

import { Button } from "@components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/ui/tabs";
import EventData from "@pages/s360/S360Results/EventData";
import CustomTooltip from "@components/common/CustomTooltip";
import {
  getEntityAttributes,
  getSystemArchitectureInfo,
  getOtherEntityAttributes,
} from "@services/api/commonApis";

import {
  convertToEpochTime,
  convertToHours,
  EpochToDateTime,
  getShadow360QueryBuilderURL,
  getTextType,
} from "@utils/utils";
// import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogHeader,
} from "@components/ui/dialog";

import DialogLoader from "@components/loaders/DialogLoader";
import CustomWidget from "@components/common/Widget";
import DataTable from "@components/common/DataTable";
import DataTableAnomaly from "./DataTableAnomaly";
import InfoTooltip from "@components/common/InfoTooltip";
import DonutChart from "@components/common/charts/DonutChart";
import DonutChartLoader from "@components/loaders/DonutChartLoader";
import BarChart from "@components/common/charts/BarChart";
import VerticalBarChart from "@components/common/charts/VerticalBarChart";
import LineChart from "@components/common/charts/LineChart";
import BarChartLoader from "@components/loaders/BarChartLoader";
import ChordDiagram from "@components/common/charts/ChordDiagram";
import SankeyGraph from "@components/common/charts/SankeyGraph";
import PluginEntityInspector from "@pages/homepage/plugins/PluginEntityInspector";
import ConnectionChart from "@components/common/charts/ConnectionGraph";
import ConnectionLoader from "@components/loaders/ConnectionLoader";
import { Switch } from "@components/ui/switch";
import { Label } from "@components/ui/label";
import AMLDropdownmenu from "../aml-anomaly-detection/AmlDropdownmenu";
import CustomSelect from "@components/common/CustomSelect";
import {
  isFeatureAllowed,
  ALL_KEYS,
  getFeatureValues,
} from "@utils/licenseUtils";
import { GeneralContext } from "@services/providers/GeneralProvider";

import {
  getHostDetailsData,
  // getEntityStatsData,
  getEntityConnctionsData,
  getEntityUserSessions,
  getEntityVulnerabilities,
  getEntityNonADUsers,
  // entityAction,
  updateEntityAttribute,
  updateAssetList,
  deleteEntityAsset,
  entityTimeSeriesAttribute,
  getEntityFlowSummaryData,
} from "@services/api/assetAutoDiscovery";
import {
  assetEntityInspectortProps,
  // statsProps,
  updateAttributeProps,
} from "./assetAutoDiscoveryTypes";
import TimeFilter from "@components/common/TimeFilter";
import {
  ExpandIcon,
  FileDown,
  InfoIcon,
  LoaderIcon,
  Minimize,
  X,
  Lock,
  LockOpen,
  Check,
  Search,
  ExternalLink,
  DownloadIcon,
  Trash,
} from "lucide-react";
import NoData from "@components/common/NoDataLabel";
import TableLoader from "@components/loaders/TableLoader";
import { useToast } from "@components/ui/use-toast";
import { Input } from "@components/ui/input";
import UserInvestigator from "@components/common/UserInvestigator";
import { Badge } from "@components/ui/badge";
import ShadowGPTFloat from "@components/common/ShadowGPTFloat";
import { systemArchitectureInfoProp } from "@components/common/CommonTypes";
import ConfirmDialog from "@components/common/ConfirmDialog";
import { ImportParserModal } from "./EntityModal";

const AssetEntityInspectorContent: React.FC<assetEntityInspectortProps> = ({
  mac,
  ipAddress,
  onClose,
  open = true,
  filter_time,
  defaultTab = "anomalies",
}) => {
  if (!open) return null;
  const { theme, isAdminUser } = useContext(GeneralContext);
  const scale = window.innerWidth / 1900;
  const nonMacAssetRef = useRef<boolean>(false);
  const { toast } = useToast();
  const [subnet, setSubnet] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userId, setUserId] = useState<string>("");
  const [summaryEditData, setSummaryEditData] = useState<any>({});
  const [savingSummary, setSavingSummary] = useState(false);
  const [eventProps, setEventProps] = useState<any>({});
  const [assetRawLog, setAssetRawLog] = useState<string>("");
  const [entityAttributeDialog, setEntityAttributeDialog] =
    React.useState(false);
  const [attributeTab, setAttributeTab] = useState<string>("default");
  const [defaultEntityAttributeRows, setDefaultEntityAttributeRows] =
    React.useState<any>([]);
  const [customEntityAttributeRows, setCustomEntityAttributeRows] =
    React.useState<any>([]);
  const [filterTime, setfilterTime] = useState<string>(
    filter_time ? filter_time : "day",
  );
  const [entityTimeSeriesDataState, setEntityTimeSeriesData] = React.useState<{
    system: any[];
    custom: any[];
  }>({
    system: [],
    custom: [],
  });

  // const [s360Url, setS360Url] = useState<string>("");
  const [inventoryData, setInventoryData] = useState<any[]>([]);
  const [alertsData, setAlertsData] = useState<any[]>([]);
  const {
    data: entityData,
    isLoading: entityLoading,
    refetch: refetchEntityData,
    isRefetching: refetchingEntity,
    isError: entityError,
  } = useQuery(["getHostDetailsData", mac, ipAddress], ({ signal }) =>
    getHostDetailsData(mac, filterTime, undefined, signal, ipAddress),
  );
  // const { data: connectionsData, isLoading: connectionsLoading } = useQuery<entityConnectionProps>(["entityConnections" , mac], () => getEntityConnctionsData(mac))
  // const {
  //   data: statsData,
  //   isLoading: statsDataLoading,
  //   refetch: refetchStats,
  //   isRefetching: refetchingStats,
  // } = useQuery<statsProps>(["entityStats", mac], ({ signal }) =>
  //   getEntityStatsData(
  //     (mac || ipAddress) as string,
  //     filterTime,
  //     mac ? "mac" : "ip",
  //     signal,
  //   ),
  // );

  const { data: systemArchitectureInfo } = useQuery<systemArchitectureInfoProp>(
    ["systemArchitectureInfo"],
    ({ signal }) => getSystemArchitectureInfo(signal),
  );

  const {
    data: connectionData,
    isFetching: connectionFetching,
    refetch: refetchConnectionData,
    isLoading: connectionLoading,
  } = useQuery(
    ["getEntityConnctionsData", mac, ipAddress],
    ({ signal }) =>
      getEntityConnctionsData(
        // (mac || ipAddress) as string,
        mac as string,
        ipAddress as string,
        subnet || "",
        filterTime,
        mac ? "mac" : "ip",
        signal,
      ),
    {
      enabled: false,
    },
  );

  const { data: userSessionData, refetch: refetchUserSessions } = useQuery(
    ["getEntityUserSessions", mac, ipAddress],
    ({ signal }) =>
      getEntityUserSessions(
        (mac || ipAddress) as string,
        filterTime,
        signal,
        mac ? "mac" : "ip",
      ),
  );

  const { data: nonADUsersData, refetch: refetchNonADUsers } = useQuery(
    ["getEntityNonADUsers", mac, ipAddress],
    ({ signal }) =>
      getEntityNonADUsers(
        (mac || ipAddress) as string,
        filterTime,
        signal,
        mac ? "mac" : "ip",
      ),
  );

  const { data: vulnerabilityData, refetch: refetchVulnerability } = useQuery(
    ["getEntityVulnerabilities", mac, ipAddress],
    ({ signal }) =>
      getEntityVulnerabilities(
        mac ? mac : "",
        ipAddress ? ipAddress : "",
        filterTime,
        mac ? "mac" : "ip",
        signal,
      ),
    {
      enabled: false,
    },
  );

  const { data: entityAttributes } = useQuery(["getEntityAttributes"], () =>
    getEntityAttributes(false),
  );

  const {
    data: otherEntityAttributes,
    isLoading: otherEntityAttributesLoading,
  } = useQuery(["getOtherEntityAttributes", mac], ({ signal }) =>
    getOtherEntityAttributes({ macAddress: mac }, signal),
  );

  const {
    data: entityFlowSummaryData,
    isLoading: entityFlowSummaryLoading,
    refetch: refetchEntityFlowSummary,
    isFetching: fetchingEntityFlowSummary,
  } = useQuery(["getEntityFlowSummaryData", mac, ipAddress], ({ signal }) =>
    getEntityFlowSummaryData(
      {
        filterTime: filterTime,
        macAddress: mac,
        ipAddress: ipAddress,
        subnet: subnet || "",
      },
      signal,
    ),
  );

  useEffect(() => {
    if (!fetchingEntityFlowSummary) {
      refetchVulnerability();
    }
  }, [fetchingEntityFlowSummary]);

  const categoryList: any = [
    { label: "Mobile", value: "Mobile" },
    { label: "Computer", value: "Computer" },
    { label: "Network Device", value: "Network Device" },
    { label: "Server", value: "Server" },
    { label: "Security Device", value: "Security Device" },
    { label: "IP Phone", value: "IP Phone" },
    { label: "IP Camera", value: "IP Camera" },
    { label: "Printer", value: "Printer" },
    { label: "Media Player", value: "Media Player" },
  ];

  const osList: any = [
    {
      label: "Unknown",
      value: "Unknown",
    },
    {
      label: "Windows",
      value: "Windows",
    },
    {
      label: "Linux",
      value: "Linux",
    },
    {
      label: "MacOs",
      value: "MacOs",
    },
    {
      label: "Android",
      value: "Android",
    },
    {
      label: "IOS",
      value: "IOS",
    },
  ];

  const endPointList: any = [
    { label: "Trend Micro", value: "Trend Micro" },
    { label: "Kaspersky", value: "Kaspersky" },
    { label: "AVG", value: "AVG" },
    { label: "Symantec", value: "Symantec" },
    { label: "McAfee", value: "McAfee" },
    { label: "Lookout", value: "Lookout" },
    { label: "Avast", value: "Avast" },
    { label: "Cortex", value: "Cortex" },
    { label: "Malwarebytes", value: "Malwarebytes" },
    { label: "Cybereason", value: "Cybereason" },
    { label: "VMware Carbon Black", value: "VMware Carbon Black" },
    { label: "Cylance", value: "Cylance" },
    { label: "SentinelOne", value: "SentinelOne" },
    { label: "Trellix ePO Cloud", value: "Trellix ePO Cloud" },
    { label: "Trellix HX Cloud", value: "Trellix HX Cloud" },
    { label: "Bitdefender", value: "Bitdefender" },
    { label: "Microsoft Defender Cloud", value: "Microsoft Defender Cloud" },
    { label: "Cisco", value: "Cisco" },
    { label: "CrowdStrike", value: "CrowdStrike" },
  ];

  const classList: any = [
    { label: "Firewall", value: "Firewall" },
    { label: "Router", value: "Router" },
    { label: "IP Phone", value: "IP Phone" },
    { label: "Switch", value: "Switch" },
    { label: "Mobile", value: "Mobile" },
    { label: "SCCM Server", value: "SCCM Server" },
    { label: "Network Access Control", value: "Network Access Control" },
    { label: "Database", value: "Database" },
    { label: "File Server", value: "File Server" },
    { label: "Web Server", value: "Web Server" },
    { label: "Linux Server", value: "Linux Server" },
    { label: "DNS Server", value: "DNS Server" },
    { label: "DHCP Server", value: "DHCP Server" },
  ];

  const watchAction = useMutation(updateAssetList, {
    onSuccess: (_, variables) => {
      let message = "Action Successful";
      if (variables.length > 0) {
        const variable = variables[0];
        if (variable.attributes[0].name == "is_watched") {
          message =
            variable.attributes[0].value == 1
              ? "Added to Watchlist"
              : "Removed from Watchlist";
          setHostDetailData((prev: any) => ({
            ...prev,
            is_watched: variable.attributes[0].value,
          }));
        } else if (variable.attributes[0].name == "whitelist_mac") {
          message =
            variable.attributes[0].value == 1
              ? "Added to Whitelist MAC"
              : "Removed from Whitelist MAC";
          setHostDetailData((prev: any) => ({
            ...prev,
            whitelist_mac: variable.attributes[0].value,
          }));
        } else if (variable.attributes[0].name == "whitelist_ip") {
          message =
            variable.attributes[0].value == 1
              ? "Added to Whitelist IP"
              : "Removed from Whitelist IP";
          setHostDetailData((prev: any) => ({
            ...prev,
            whitelist_ip: variable.attributes[0].value,
          }));
        } else if (variable.attributes[0].name == "is_critical") {
          message =
            variable.attributes[0].value == 1
              ? "Marked as Critical"
              : "Removed from Critical";
          setHostDetailData((prev: any) => ({
            ...prev,
            is_critical: variable.attributes[0].value,
          }));
        }
      }
      toast({
        title: "Success",
        description: message,
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  const deleteAssetMutation = useMutation(deleteEntityAsset, {
    onSuccess: (res) => {
      if (res?.status) {
        toast({
          title: "Success",
          description: res?.message || "Deleted Asset Successfully",
          variant: "success",
        });
        if (onClose) {
          onClose();
        }
      } else {
        toast({
          title: "Error",
          description: res?.message || "An error occured!",
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
    },
  });

  const getEntityTimeSeriesAttribute = useMutation(entityTimeSeriesAttribute, {
    onSuccess: (res) => {
      const arrayDataSystem = Object.entries(res?.data?.system || {}).map(
        ([attribute_name, value]: [string, any]) => ({
          attribute_name,
          attribute_value: value.value,
        }),
      );

      const arrayDataCustom = Object.entries(res?.data?.custom || {}).map(
        ([attribute_name, value]: [string, any]) => ({
          attribute_name,
          attribute_value: value.value,
        }),
      );
      setEntityTimeSeriesData({
        system: arrayDataSystem,
        custom: arrayDataCustom,
      });
    },
  });

  const watchEditEntity = useMutation(updateEntityAttribute, {
    onSuccess: () => {
      refetchEntityData();
      const message = "Action Successful";
      toast({
        title: "Success",
        description: message,
        variant: "success",
      });
      setSavingSummary(false);
      setSummaryEditData({});
      refetchConnectionData();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "An error occured!",
        variant: "destructive",
      });
      setSavingSummary(false);
    },
  });

  const [statsData, setStatsData] = useState<any>({});
  const [nodes, setNodes] = useState<any[]>([]);
  const [links, setLinks] = useState<any[]>([]);
  const [dhcpSessionsData, setdhcpSessionsData] = useState<any[]>([]);
  const [userAccessData, setUserAccessData] = useState<any[]>([]);
  const [outdatedSoftwaresData, setOutdatedSoftwaresData] = useState<any[]>([]);
  const [vulnerabilitiesData, setVulnerabilitiesData] = useState<any[]>([]);
  const [networkUsersData, setNonADUsersData] = useState<any[]>([]);
  const [trafficPattern, setTrafficPattern] = useState<any[]>([]);
  const [trafficLabels, setTrafficLabels] = useState<any[]>([]);
  const [anomaliesData, setAnomaliesData] = useState<any[]>([]);
  const [anomalyLineData, setAnomalyLineData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState<any>({});
  const [threatScore, setThreatScore] = useState<number>(0);
  const [thirdPartyScore, setThirdPartyScore] = useState<number>(0);
  const [hostDetailData, setHostDetailData] = useState<any>({});
  const [showRelatedMacDialog, setShowRelatedMacDialog] =
    useState<boolean>(false);
  const [assetEntityAttributes, setAssetEntityAttributes] =
    useState<boolean>(false);
  const [attributeModalData, setAttributeModalData] = useState<any>(null);
  const relatedMacTableData = useMemo(() => {
    if (!Array.isArray(hostDetailData?.relatedMacList)) {
      return [];
    }

    return hostDetailData.relatedMacList
      .map((macItem: any) => String(macItem || "").trim())
      .filter((macItem: string) => macItem !== "")
      .map((macItem: string) => ({
        relatedMac: (
          <div className="max-w-[70vw] sm:max-w-[380px] break-all">
            {macItem}
          </div>
        ),
      }));
  }, [hostDetailData?.relatedMacList]);

  // function toggleClass(element: any, cls: string) {
  //   const allElements = document.getElementsByClassName(cls);
  //   for (let i = 0; i < allElements.length; i++) {
  //     allElements[i].classList.add("hidden");
  //   }

  //   if (element.classList.contains(cls)) {
  //     element.classList.remove(cls);
  //   } else {
  //     element.classList.add(cls);
  //   }
  // }

  const topServicesBandwidthData = useMemo(() => {
    if (!statsData?.protocol_bandwidth) return [];

    return statsData.protocol_bandwidth.map((e: any) => ({
      label: e.name,
      value: e.value,
    }));
  }, [statsData?.protocol_bandwidth]);

  const topNoisyServicesData = useMemo(() => {
    if (!statsData?.protocol_count) return [];

    return statsData.protocol_count.map((e: any) => ({
      label: e.name,
      value: e.value,
    }));
  }, [statsData?.protocol_count]);

  useEffect(() => {
    if (otherEntityAttributes) {
      const mergedAttributes = [
        ...(otherEntityAttributes.system || []),
        ...(otherEntityAttributes.custom || []),
        ...entityTimeSeriesDataState.system,
        ...entityTimeSeriesDataState.custom,
      ];
      const temp: any = [];

      mergedAttributes.forEach((element: any) => {
        const displayValue = element.attribute_value ?? "- - -";

        temp.push({
          attribute: (
            <div className="capitalize">
              {element.display_name ||
                element.attribute_name.split("_").join(" ")}
            </div>
          ),

          value: (
            <div className="flex items-center gap-2">
              <span>
                {Array.isArray(element.attribute_value)
                  ? element.attribute_value.every(
                      (item: any) => typeof item === "string",
                    )
                    ? element.attribute_value.join(", ")
                    : "View Details"
                  : String(displayValue).length > 50
                    ? `${String(displayValue).slice(0, 50)}...`
                    : String(displayValue)}
              </span>

              {Array.isArray(displayValue) && (
                <CustomTooltip text="View All Attributes" side="top">
                  <Search
                    size={16}
                    className="cursor-pointer"
                    onClick={() => {
                      setAttributeModalData(element.attribute_value);
                      setAssetEntityAttributes(true);
                    }}
                  />
                </CustomTooltip>
              )}
            </div>
          ),
        });
      });

      setCustomEntityAttributeRows(temp);
    }
  }, [otherEntityAttributes, entityTimeSeriesDataState]);

  useEffect(() => {
    if (hostDetailData && Object.keys(hostDetailData).length > 0) {
      //Entity Attributes
      if (hostDetailData && "computerAttributes" in hostDetailData) {
        const tempDefault: any = [];
        const attrs = hostDetailData.computerAttributes;
        Object.keys(attrs).forEach((key: string) => {
          tempDefault.push({
            attribute: key.split("_").join(" "),
            value:
              typeof attrs[key] == "string"
                ? attrs[key]
                : JSON.stringify(attrs[key]),
          });
        });

        setDefaultEntityAttributeRows(tempDefault);
      }

      const anomalies: any = entityData.anomalies;
      const temp: any = [];
      let total_score: number = 0;
      anomalies.forEach((anomaly: any) => {
        total_score = 0;
        const passdata = anomaly;
        passdata["anomalyId"] = passdata.anomaly_id;
        // passdata["anomalyId"] = passdata.id;
        passdata["globalID"] = passdata.id;
        passdata["time_seen"] = passdata.epochTime;
        passdata["EventNames"] = passdata["data"]["events"].map(
          (ev: any, i: any) => ({ [i]: ev.EventId }),
        );
        if (
          anomaly &&
          anomaly.anomaly_group &&
          anomaly.anomaly_group.length > 0
        ) {
          total_score = anomaly.anomaly_group.reduce(
            (sum: number, item: any) => sum + (item.score || 0),
            anomaly.score ?? 0,
          );
        }
        // for anomlay group

        const events = anomaly.data.events;
        let anomalyDetails: any = null;
        const anomalyDetailstemp = events.map((event: any, indx: number) => {
          const eventdata = JSON.parse(event.EventData);
          const eventProps = {
            anomalousType: "entity",
            sensor: eventdata.sensor,
            eventId: eventdata.event_id,
            eventGid: eventdata.GlobalID,
            eventTime: eventdata.time_seen,
            anomalousIp: eventdata.anomalous_ip,
            anomalousMac: eventdata.anomalous_ip_mac,
          };
          return (
            <li key={indx}>
              <span
                className="border-b pe-4 cursor-pointer"
                onClick={() => {
                  setEventProps(eventProps);
                }}
              >
                {eventdata.anomaly_type}
              </span>
            </li>
          );
        });

        anomalyDetails = (
          <div>
            <div className="font-bold border-b inline-block mb-1 pb-1">
              Events List
            </div>
            <ul className="list-disc">{anomalyDetailstemp}</ul>
          </div>
        );

        const eventNames: any = {};
        if ("data" in anomaly && "events" in anomaly.data) {
          anomaly.data.events.forEach((event: any) => {
            eventNames[event.EventId] = event.EventName;
          });
        }

        const eventPop = (
          <div className="ps-2">
            <div className="font-bold border-b inline-block mb-1 pb-1">
              Events List
            </div>
            <ul className="list-disc ps-4">
              {anomaly.data.events.map((event: any, id: number) => {
                const eventdata = JSON.parse(event.EventData);
                const eventProps = {
                  anomalousType: "entity",
                  sensor: eventdata.sensor,
                  eventId: event.EventId,
                  eventGid: eventdata.GlobalID,
                  eventTime: eventdata.time_seen,
                  anomalousIp: eventdata.anomalous_ip,
                  anomalousMac: eventdata.anomalous_ip_mac,
                };
                return (
                  <li
                    key={id}
                    className="pe-4 cursor-pointer hover:bg-secondary"
                    onClick={() => setEventProps(eventProps)}
                  >
                    {event.EventName || eventdata.anomaly_type}
                  </li>
                );
              })}
            </ul>
          </div>
        );

        temp.push({
          data: anomaly,
          category: (
            <div className="whitespace-nowrap align-middle flex items-center gap-2">
              <div>
                <AMLDropdownmenu
                  event_names={eventNames}
                  anomalyId={anomaly.id}
                  anomaly_type={anomaly.anomalyType}
                  time_seen={anomaly.epochTime}
                  events={anomaly.data.events}
                  isModal={true}
                  filterTime={{
                    filterTime: filterTime,
                    CustomFilter: "",
                    fromDate: "",
                    toDate: "",
                  }}
                />
              </div>
              <span className="inline-block align-top">{anomaly.category}</span>
            </div>
          ),
          type: anomaly.type,
          description: <div className="max-w-[40vw]">{anomaly.desc}</div>,
          events: (
            <CustomTooltip text={eventPop} side="right">
              <span className="bg-primary text-white px-2 py-1 rounded-md cursor-pointer whitespace-nowrap overflow-x-visible">
                {anomaly.data.events.length}{" "}
                {anomaly.data.events.length == 1 ? "Event" : "Events"}
              </span>
            </CustomTooltip>
          ),
          score: (
            <Badge className="rounded-sm px-1 bg-primary">
              {total_score > 0 ? total_score : anomaly.score}
            </Badge>
          ),
          // score: anomaly.score,
          time: <span>{EpochToDateTime(anomaly.epochTime)}</span>,
          dtime: <span>{anomaly.time}</span>,
          groupAnom:
            anomaly && anomaly.anomaly_group && anomaly.anomaly_group.length > 0
              ? true
              : false,
          expandDetails: anomalyDetails,
          expandAnomaly:
            anomaly && anomaly.anomaly_group && anomaly.anomaly_group.length > 0
              ? [...anomalies, ...anomaly.anomaly_group]
              : "",
        });
      });

      setAnomaliesData(temp);
      actionData["watched"] = entityData.watched;
      actionData["whitelistMac"] = entityData.whitelist_mac;
      actionData["whitelistIp"] = entityData.whitelist_ip;
      actionData["critical"] = entityData.critical;
      setActionData(actionData);

      //Anomaly Line graph
      const lineChartData: { x: string; y: number }[] = [];
      const seenHours: Record<string, number> = {};

      anomalies.forEach((element: any) => {
        const timeStr: string = element["time"];
        const hourBucket = timeStr.split(":")[0] + ":00:00";

        if (hourBucket in seenHours) {
          seenHours[hourBucket] += 1;
          const existing = lineChartData.find((d) => d.x === hourBucket);
          if (existing) existing.y = seenHours[hourBucket];
        } else {
          seenHours[hourBucket] = 1;
          lineChartData.push({ x: hourBucket, y: 1 });
        }
      });

      setAnomalyLineData(lineChartData);

      //Summary
      const summary: any = {};
      summary["IP"] = {
        name: entityData.ip_addr || entityData.ip,
      };
      summary["Hostname"] = {
        name: entityData.hostname || "",
        keyTooltip: (
          <div>The asset hostname, or unique identifier name of this host</div>
        ),
        editable: true,
        fieldType: "string",
        fieldName: "hostname",
        title: "Hostname",
      };
      summary["MAC"] = {
        name: entityData.mac_addr || mac,
      };
      if (entityData.mac_addr && entityData.mac_addr != "") {
        nonMacAssetRef.current = false;
      } else {
        nonMacAssetRef.current = true;
      }
      summary["MAC Vendor"] = {
        name: entityData.macVendor,
      };
      summary["First Seen"] = {
        name: entityData.firstSeen,
      };
      summary["Last Seen"] = {
        name: entityData.lastSeen,
      };

      summary["Category"] = {
        name: entityData.entityType,
        keyTooltip: "Device type of this asset",
        toolTip: (
          <div className="p-2">
            <div className="font-bold border-b mb-1 pb-1">Category</div>
            <ul className="list-disc ps-4">
              {Object.keys(entityData.entityClassificationList).map(
                (key: string, indx: number) => {
                  const osData = entityData.entityClassificationList[key];
                  return osData.map((os: any) => (
                    <li key={indx}>
                      <span>{os.category1}</span>
                      {"source" in os && <span> ({os.source})</span>}
                    </li>
                  ));
                },
              )}
            </ul>
          </div>
        ),
        editable: true,
        fieldType: "dropdown",
        fieldOptions: categoryList,
        fieldName: "classification",
        title: "Category",
      };
      summary["OS"] = {
        name: entityData.os,
        toolTip: (
          <div className="p-2">
            <div className="font-bold border-b mb-1 pb-1">Operating System</div>
            <ul className="list-disc ps-4">
              {Object.keys(entityData.entityOsList).map(
                (key: string, indx: number) => {
                  const osData = entityData.entityOsList[key];
                  return osData.map((os: any) => (
                    <li key={indx}>
                      <span>{os.category1}</span>
                      {"category2" in os && <span> {os.category2}</span>}
                      {"source" in os && <span> ({os.source})</span>}
                    </li>
                  ));
                },
              )}
            </ul>
          </div>
        ),
        keyTooltip: <div>Operating System running on this asset</div>,
        editable: true,
        fieldType: "dropdown",
        fieldOptions: osList,
        fieldName: "operating_system",
        title: "Operating System",
      };
      summary["Type"] = {
        name: (
          <div>
            {entityData.is_managed && entityData.is_managed == 1 ? (
              <Check size={14} className="text-green-500 inline-block me-2" />
            ) : (
              <X size={14} className="text-primary inline-block me-2" />
            )}
            <span>
              {entityData.is_managed && entityData.is_managed == 1
                ? "Managed"
                : "Unmanaged"}
            </span>
          </div>
        ),
      };
      summary["Endpoint Security"] = {
        name: (
          <div>
            {(entityData.endpointSecurity
              ? entityData.endpointSecurity
              : "None") == "None" ? (
              <LockOpen size={14} className="text-primary inline-block me-2" />
            ) : (
              <Lock size={14} className="text-primary inline-block me-2" />
            )}
            <span>{entityData.endpointSecurity || "None"}</span>
          </div>
        ),
        toolTip: (
          <div className="p-2">
            <div className="font-bold border-b mb-1 pb-1">
              Endpoint Security
            </div>
            <ul className="list-disc ps-4">
              {Object.keys(entityData.entityEndpointSecurityList).map(
                (key: string, indx: number) => {
                  const epData = entityData.entityEndpointSecurityList[key];
                  return epData.map((ep: any) => (
                    <li key={indx}>
                      <span>{ep.category1}</span>
                      {"category2" in ep && <span> {ep.category2}</span>}
                      {"source" in ep && <span> ({ep.source})</span>}
                    </li>
                  ));
                },
              )}
            </ul>
          </div>
        ),
        editable: true,
        fieldType: "dropdown",
        fieldOptions: endPointList,
        fieldName: "endpoint_security",
        title: "Endpoint Security",
        defaultValue: entityData.endpointSecurity,
      };
      summary["Subnet"] = {
        name: entityData.subnet_mask
          ? getTextType(entityData.subnet) == "ip"
            ? entityData.subnet + "/" + entityData.subnet_mask
            : entityData.subnet
          : entityData.subnet,
      };
      const relatedMacList =
        Array.isArray(entityData.relatedMacList) &&
        entityData.relatedMacList.length > 0
          ? entityData.relatedMacList
              .map((macItem: any) => String(macItem || "").trim())
              .filter((macItem: string) => macItem !== "")
          : [];

      summary["Related MAC"] = {
        name:
          relatedMacList.length > 0 ? (
            <div className="whitespace-normal break-words">
              <span>
                {relatedMacList.slice(0, 3).join(", ")}
                {relatedMacList.length > 3 ? "..." : ""}
              </span>
              {relatedMacList.length > 3 && (
                <Button
                  variant="link"
                  className="h-auto p-0 mt-1 text-xs block"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRelatedMacDialog(true);
                  }}
                >
                  View More
                </Button>
              )}
            </div>
          ) : (
            "Not Available"
          ),
      };
      summary["Department"] = {
        name: entityData.department,
        editable: true,
        fieldType: "string",
        fieldName: "department",
        title: "Department",
      };

      const distType = systemArchitectureInfo
        ? systemArchitectureInfo.distributedSystemsType
        : "";
      if (distType != "mssp" && distType != "standalone") {
        summary["Distributed System"] = {
          name: entityData.distributed_system,
        };
      }

      summary["Tags"] = {
        name:
          JSON.parse(entityData.tags)?.length > 0
            ? JSON.parse(entityData.tags).join(", ")
            : "",
        editable: true,
        fieldType: "string",
        fieldName: "tags",
        title: "Tags",
      };
      // summary["Classification"] = {
      summary["Sub Category"] = {
        name: entityData.classificationType,
        editable: true,
        fieldType: "dropdown",
        fieldOptions: classList,
        fieldName: "category",
        // title: "Classification",
        title: "Sub Category",
      };
      summary["Comments"] = {
        name: entityData.comment,
        editable: true,
        fieldType: "string",
        fieldName: "comment",
        title: "Comments",
        defaultValue: entityData.comment || " ",
      };
      setThreatScore(entityData.threatScore);
      setSummaryData(summary);

      const alertTemp: any = [];
      entityData.alerts.forEach((alert: any) => {
        const eventNames: Record<string, string | number> = {};
        alert.data.events.forEach((event: any) => {
          eventNames[event.EventId] =
            event.EventName ?? event.EventId.toString();
        });

        const eventPop = (
          <div className="ps-2">
            <div className="font-bold border-b inline-block mb-1 pb-1">
              Events List
            </div>
            <ul className="list-disc ps-4">
              {alert.data.events.map((event: any, id: number) => {
                const eventdata = JSON.parse(event.EventData || "{}");
                const eventProps = {
                  anomalousType: "entity",
                  sensor: eventdata.sensor,
                  eventId: eventdata.event_id,
                  eventGid: eventdata.GlobalID,
                  eventTime: eventdata.time_seen,
                  anomalousIp: eventdata.anomalous_ip,
                  anomalousMac: eventdata.anomalous_ip_mac,
                };
                return (
                  <li
                    key={id}
                    className="pe-4 cursor-pointer hover:bg-secondary"
                    onClick={() => setEventProps(eventProps)}
                  >
                    {event.EventName || eventdata.anomaly_type}
                  </li>
                );
              })}
            </ul>
          </div>
        );

        alertTemp.push({
          category: (
            <div className="whitespace-nowrap align-middle flex items-center gap-2">
              <div>
                <AMLDropdownmenu
                  event_names={eventNames}
                  anomalyId={alert.id || alert.GlobalID}
                  anomaly_type={(
                    alert.anomalyType ||
                    alert.anomaly_type ||
                    ""
                  ).toLowerCase()}
                  time_seen={alert.epochTime}
                  events={alert.data?.events}
                  isModal={true}
                  dropdownType="alert"
                  filterTime={{
                    filterTime: filterTime,
                    CustomFilter: "",
                    fromDate: "",
                    toDate: "",
                  }}
                />
              </div>
              <span className="inline-block align-top">{alert.category}</span>
            </div>
          ),
          type: alert.type,
          remoteIpMac:
            (alert.dip || alert.anomalous_ip || "- - -") +
            " / " +
            (alert.dmac || alert.anomalous_ip_mac || "- - -"),
          desc: alert.desc || "- - -",
          time: alert.time,
          score: (
            <div className="bg-primary text-white rounded-sm inline-block py-1 px-2">
              {alert.score}
            </div>
          ),
          sensor: alert.sensor,
          data: {
            globalID: alert.GlobalID || alert.id,
            epochTime: alert.epochTime,
          },
          events: (
            <CustomTooltip text={eventPop} side="right">
              <span className="bg-primary text-white px-2 py-1 rounded-md cursor-pointer whitespace-nowrap overflow-x-visible">
                {alert.data.events.length}{" "}
                {alert.data.events.length == 1 ? "Event" : "Events"}
              </span>
            </CustomTooltip>
          ),
        });
      });
      setAlertsData(alertTemp);
    }
  }, [hostDetailData, systemArchitectureInfo]);

  useEffect(() => {
    if (entityData) {
      setHostDetailData(entityData);
    }
  }, [entityData, editing, entityAttributes]);

  useEffect(() => {
    if (connectionData) {
      updateConnectionData(connectionData);
    }
  }, [connectionData]);

  useEffect(() => {
    if (
      entityFlowSummaryData &&
      Object.keys(entityFlowSummaryData).length > 0
    ) {
      // if (entityFlowSummaryData.entity_vulnerabilities) {
      //   const vulnerabilities = entityFlowSummaryData.entity_vulnerabilities;
      //   const appNames: any = [];
      //   const vulnData: any = [];

      //   const ap = vulnerabilities["app"] || {};
      //   const browers = vulnerabilities["browser"] || {};
      //   const app1 = { ...ap, ...browers };

      //   Object.keys(app1).forEach((key2: string) => {
      //     const app = app1[key2];
      //     for (const appdata of app) {
      //       if (
      //         appNames.findIndex(
      //           (item: any) =>
      //             item.app == appdata.name && item.version == appdata.version,
      //         ) == -1
      //       ) {
      //         appNames.push({
      //           app: appdata.name,
      //           version: appdata.version,
      //         });
      //       }

      //       const vuln = appdata;
      //       vulnData.push({
      //         type: "App",
      //         name: vuln?.name,
      //         version: vuln?.version,
      //         cve: vuln?.cve,
      //         description: vuln?.description,
      //         severity: vuln?.impact?.baseMetricV2?.severity,
      //         impactScore: (
      //           <div className="bg-primary px-1 min-w-6 text-center rounded-sm text-white inline-block">
      //             {vuln?.impact?.baseMetricV2?.impactScore}
      //           </div>
      //         ),
      //         baseScore: (
      //           <div className="bg-primary px-1 min-w-6 text-center rounded-sm text-white inline-block">
      //             {vuln?.impact?.baseMetricV2?.cvssV2?.baseScore}
      //           </div>
      //         ),
      //       });
      //     }
      //   });

      //   setOutdatedSoftwaresData(appNames);
      //   setVulnerabilitiesData(vulnData);
      // }

      if (entityFlowSummaryData.entity_connections) {
        const connectionData = entityFlowSummaryData.entity_connections;
        updateConnectionData(connectionData);
      }

      if (entityFlowSummaryData.entity_status) {
        setStatsData(entityFlowSummaryData.entity_status);
        const _statsData = entityFlowSummaryData.entity_status;
        const time_series = _statsData.protocol_bandwidth_time_series;
        const temp: any = [];

        const temp_data: { label: string; value: number }[] = [];
        time_series.forEach((eachData: any) => {
          const existingItem = temp_data.find(
            (item) => item.label === eachData.type,
          );
          if (existingItem) {
            existingItem.value += eachData.value;
          } else {
            temp_data.push({ label: eachData.type, value: eachData.value });
          }
        });

        temp_data.sort((a, b) => b.value - a.value);

        const labels = temp_data.slice(0, 10).map((item) => item.label);

        labels.forEach((element: string) => {
          const line: any = [];
          time_series.forEach((data: any) => {
            if (data.type == element) {
              const date = new Date(data.date);
              const milliseconds = date.getTime();
              const seconds = Math.floor(milliseconds / 1000);
              line.push({
                x: seconds,
                y: data.value,
              });
            }
          });
          temp.push(line);
        });

        setTrafficPattern(temp);
        setTrafficLabels(labels);

        setThirdPartyScore(_statsData.external_detection_score);

        const app_count_temp: { app: any; versions: any }[] = [];
        _statsData?.app_count?.map((item: any) => {
          app_count_temp.push({
            app: item[0],
            versions: item[1],
          });
        });
        setInventoryData(app_count_temp || []);
      }
    }
  }, [entityFlowSummaryData]);

  const updateConnectionData = (connectionData: any) => {
    const tempNodes: any = [];
    const tempLinks: any = [];

    tempNodes.push({
      id: connectionData.hostname
        ? connectionData.hostname
        : connectionData.ip
          ? connectionData.ip
          : connectionData.mac,
      type: connectionData.entityType,
      group: 1,
      level: 1,
      entityType: connectionData.entityType,
      ip: connectionData.ip,
      hostname: connectionData.hostname
        ? connectionData.hostname
        : connectionData.ip,
      mac: connectionData.mac,
    });
    if (connectionData.hasinternet == 1) {
      tempNodes.push({
        id: "Internet",
        type: "internet",
        group: 1,
        level: 2,
        entityType: "Internet",
        lastSeenIP: connectionData.lastInternetIp,
      });
      tempLinks.push({
        source: connectionData.hostname
          ? connectionData.hostname
          : connectionData.ip
            ? connectionData.ip
            : connectionData.mac,
        target: "Internet",
      });
    }
    connectionData.hostcons.forEach((host: any) => {
      let hostnameNew = host.hostname ? host.hostname : "";
      if (hostnameNew == "") {
        hostnameNew = host.entityIp;
      }
      if (host.entityType == "subnet") {
        tempNodes.push({
          id: host.hostname,
          type: host.entityType,
          mac: "",
          group: 1,
          level: 2,
          altname: host.altname,
        });
        tempLinks.push({
          source: connectionData.hostname
            ? connectionData.hostname
            : connectionData.ip,
          target: host.hostname,
        });
      } else if (host.type == "prediction") {
        tempNodes.push({
          id: host.entityIp + "_" + (host.type || "unknown"),
          altname: host.entityIp,
          type: host.entityType,
          mac: "",
          group: 1,
          level: 3,
          hostThreatScore: 0,
          conntype: host.type,
          path: host.path,
          allowed_app: host.allowed_app,
        });
        tempLinks.push({
          source: host.subnet,
          target: host.entityIp + "_" + (host.type || "unknown"),
          conntype: host.type,
        });
      } else if (host.type == "vulnerability") {
        tempNodes.push({
          id: host.entityIp + "_" + (host.type || "unknown"),
          altname: host.entityIp,
          type: host.entityType,
          mac: "",
          group: 1,
          level: 3,
          hostThreatScore: 0,
          conntype: host.type,
          app_version: host.app_version,
          vul_id_list: host.vul_id_list,
        });
        tempLinks.push({
          source: host.subnet,
          target: host.entityIp + "_" + (host.type || "unknown"),
          conntype: host.type,
        });
      } else {
        if (host.hostname != "" && host.hostname != null) {
          tempNodes.push({
            id: host.hostname + "_" + (host.type || "unknown"),
            altname: host.hostname,
            hostname: host.hostname,
            ip: host.entityIp,
            type: host.entityType ? host.entityType : "Computer",
            mac: host.mac,
            group: 1,
            level: 3,
            hostThreatScore: host.hostThreatScore,
            conntype: host.type,
            subnet: host.subnet,
          });
        } else {
          tempNodes.push({
            id: host.entityIp + "_" + (host.type || "unknown"),
            altname: host.entityIp,
            hostname: "",
            ip: host.entityIp,
            type: host.entityType ? host.entityType : "Computer",
            mac: host.mac,
            group: 1,
            level: 3,
            hostThreatScore: host.hostThreatScore,
            conntype: host.type,
            subnet: host.subnet,
          });
        }
        tempLinks.push({
          source: host.subnet,
          target:
            (host.hostname != "" ? host.hostname : host.entityIp) +
            "_" +
            (host.type || "unknown"),
          conntype: host.type,
        });
      }
    });
    if (subnet) {
      setNodes((prev: any) => [...prev, ...tempNodes]);
      setLinks((prev: any) => [...prev, ...tempLinks]);
    } else {
      setNodes(tempNodes);
      setLinks(tempLinks);
    }
  };

  // function editField(title: string, fieldName: string, defValue: any) {
  //   if (!editing) {
  //     return;
  //   }
  //   if (title == "Tags") {
  //     setNewTags(defValue.split(","));
  //   }
  //   setSummaryEditData({
  //     title: title,
  //     fieldName: fieldName,
  //     defaultValue: defValue,
  //     value: defValue,
  //   });
  // }

  function saveEntityData() {
    if (savingSummary) {
      return;
    }
    // setSavingSummary(true);
    if (summaryEditData["fieldType"] == "string") {
      let newvalue = summaryEditData["newvalue"];
      if (summaryEditData["title"] == "Tags") {
        newvalue = summaryEditData["newvalue"]
          .split(",")
          .map((tag: string) => tag.trim());
      }
      const passdata: updateAttributeProps = {
        macAddress: mac,
        attributes: [
          {
            name: summaryEditData.fieldName,
            isCustom: false,
            value: newvalue,
          },
        ],
      };
      watchEditEntity.mutate(passdata);
    } else if (summaryEditData["fieldType"] == "dropdown") {
      const passdata: any = {
        macAddress: mac,
        attributes: [
          {
            name: summaryEditData.fieldName,
            isCustom: false,
            value: {
              category1: summaryEditData["newvalue"],
              firstSeen: Math.floor(Date.now() / 1000),
              lastSeen: Math.floor(Date.now() / 1000),
              probability: 100,
              displaySource: "User",
              source: "user",
              subSource: "user",
            },
          },
        ],
      };

      const name = summaryEditData["fieldName"];
      if (name == "category") {
        const catName = summaryData["Category"]["name"];
        if (catName == "") {
          toast({
            title: "Error",
            description: "Please select a category",
            variant: "destructive",
          });
          return;
        }

        passdata.attributes[0].name = "classification";
        passdata.attributes[0].value.category1 = catName;
        passdata.attributes[0].value.category2 = summaryEditData["newvalue"];
      }

      watchEditEntity.mutate(passdata);
    }
  }

  // const updateSummaryData = useMutation(updateAssetList, {
  //   onSuccess: (data) => {
  //     const message = "Action Successful";
  //     const status = data.status;
  //     if (status == "failure") {
  //       toast({
  //         title: "Error",
  //         description: data.msg,
  //         variant: "destructive",
  //       });
  //     } else {
  //       if (summaryEditData["title"] == "Tags") {
  //         summaryData["Tags"] = newTags.join(", ");
  //       } else {
  //         summaryData[summaryEditData["title"]] = summaryEditData["value"];
  //       }
  //       toast({
  //         title: "Success",
  //         description: message,
  //         variant: "success",
  //       });
  //     }
  //     refetchEntityData();
  //     setSavingSummary(false);
  //   },
  //   onError: () => {
  //     toast({
  //       title: "Error",
  //       description: "An error occured!",
  //       variant: "destructive",
  //     });
  //     setSavingSummary(false);
  //   },
  // });

  // useEffect(() => {
  //   if (statsData) {
  //     const time_series = statsData.protocol_bandwidth_time_series;
  //     const temp: any = [];
  //     // let labels: any = [];
  //     // time_series.forEach((data: any) => {
  //     //   if (!(data.type in labels)) {
  //     //     labels.push(data.type);
  //     //   }
  //     // });

  //     // if (labels.length > 10) {
  //     //   labels = labels.splice(0, 10);
  //     // }

  //     // labels.forEach((element: string) => {
  //     //   const line: any = [];
  //     //   time_series.forEach((data: any) => {
  //     //     if (data.type == element) {
  //     //       const date = new Date(data.date);
  //     //       const milliseconds = date.getTime();
  //     //       const seconds = Math.floor(milliseconds / 1000);

  //     //       line.push({
  //     //         x: seconds,
  //     //         y: data.value,
  //     //       });
  //     //     }
  //     //   });
  //     //   temp.push(line);
  //     // });

  //     const temp_data: { label: string; value: number }[] = [];
  //     time_series.forEach((eachData: any) => {
  //       const existingItem = temp_data.find(
  //         (item) => item.label === eachData.type,
  //       );
  //       if (existingItem) {
  //         existingItem.value += eachData.value;
  //       } else {
  //         temp_data.push({ label: eachData.type, value: eachData.value });
  //       }
  //     });

  //     temp_data.sort((a, b) => b.value - a.value);

  //     const labels = temp_data.slice(0, 10).map((item) => item.label);

  //     labels.forEach((element: string) => {
  //       const line: any = [];
  //       time_series.forEach((data: any) => {
  //         if (data.type == element) {
  //           const date = new Date(data.date);
  //           const milliseconds = date.getTime();
  //           const seconds = Math.floor(milliseconds / 1000);
  //           line.push({
  //             x: seconds,
  //             y: data.value,
  //           });
  //         }
  //       });
  //       temp.push(line);
  //     });

  //     setTrafficPattern(temp);
  //     setTrafficLabels(labels);

  //     setThirdPartyScore(statsData.external_detection_score);

  //     const app_count_temp: { app: any; versions: any }[] = [];
  //     statsData?.app_count?.map((item: any) => {
  //       app_count_temp.push({
  //         app: item[0],
  //         versions: item[1],
  //       });
  //     });
  //     setInventoryData(app_count_temp || []);
  //   }
  // }, [statsData]);

  useEffect(() => {
    if (nonADUsersData) {
      const temp: any = [];
      nonADUsersData.nonADUsers.forEach((user: any) => {
        temp.push({
          username: user.username,
          lastSeen: user.last_seen,
          source: user.account_type,
        });
      });

      setNonADUsersData(temp);
    }
  }, [nonADUsersData]);

  useEffect(() => {
    if (vulnerabilityData) {
      const vulnerabilities = vulnerabilityData.vulnerablities;
      const appNames: any = [];
      const vulnData: any = [];

      const ap = vulnerabilities["app"] || {};
      const browers = vulnerabilities["browser"] || {};
      const app1 = { ...ap, ...browers };

      Object.keys(app1).forEach((key2: string) => {
        const app = app1[key2];
        for (const appdata of app) {
          if (
            appNames.findIndex(
              (item: any) =>
                item.app == appdata.name && item.version == appdata.version,
            ) == -1
          ) {
            appNames.push({
              app: appdata.name,
              version: appdata.version,
            });
          }

          const vuln = appdata;
          vulnData.push({
            type: "App",
            name: vuln?.name,
            version: vuln?.version,
            cve: vuln?.cve,
            description: vuln?.description,
            severity: vuln?.impact?.baseMetricV2?.severity,
            impactScore: (
              <div className="bg-primary px-1 min-w-6 text-center rounded-sm text-white inline-block">
                {vuln?.impact?.baseMetricV2?.impactScore}
              </div>
            ),
            baseScore: (
              <div className="bg-primary px-1 min-w-6 text-center rounded-sm text-white inline-block">
                {vuln?.impact?.baseMetricV2?.cvssV2?.baseScore}
              </div>
            ),
          });
        }
      });

      setOutdatedSoftwaresData(appNames);
      setVulnerabilitiesData(vulnData);
    }
  }, [vulnerabilityData]);

  useEffect(() => {
    if (userSessionData) {
      const dhcpSessions = userSessionData.dhcpSessions;
      const temp: any = [];
      dhcpSessions.forEach((session: any) => {
        temp.push({
          firstSeen: session.firstSeen,
          lastUpdatedTime: session.lastUpdatedTime,
          assignedIp: session.assignedIp,
          source: session.source,
        });
      });

      setdhcpSessionsData(temp);
      setUserAccessData(userSessionData.userSessions);
    }
  }, [userSessionData]);

  // useEffect(() => {
  //   if (connectionData) {
  //     const tempNodes: any = [];
  //     const tempLinks: any = [];
  //     tempNodes.push({
  //       id: connectionData.hostname
  //         ? connectionData.hostname
  //         : connectionData.ip
  //           ? connectionData.ip
  //           : connectionData.mac,
  //       type: connectionData.entityType,
  //       group: 1,
  //       level: 1,
  //       entityType: connectionData.entityType,
  //       ip: connectionData.ip,
  //       hostname: connectionData.hostname
  //         ? connectionData.hostname
  //         : connectionData.ip,
  //       mac: connectionData.mac,
  //     });
  //     if (connectionData.hasinternet == 1) {
  //       tempNodes.push({
  //         id: "Internet",
  //         type: "internet",
  //         group: 1,
  //         level: 2,
  //         entityType: "Internet",
  //         lastSeenIP: connectionData.lastInternetIp,
  //       });
  //       tempLinks.push({
  //         source: connectionData.hostname
  //           ? connectionData.hostname
  //           : connectionData.ip
  //             ? connectionData.ip
  //             : connectionData.mac,
  //         target: "Internet",
  //       });
  //     }
  //     connectionData.hostcons.forEach((host: any) => {
  //       let hostnameNew = host.hostname ? host.hostname : "";
  //       if (hostnameNew == "") {
  //         hostnameNew = host.entityIp;
  //       }
  //       if (host.entityType == "subnet") {
  //         tempNodes.push({
  //           id: host.hostname,
  //           type: host.entityType,
  //           mac: "",
  //           group: 1,
  //           level: 2,
  //           altname: host.altname,
  //         });
  //         tempLinks.push({
  //           source: connectionData.hostname
  //             ? connectionData.hostname
  //             : connectionData.ip,
  //           target: host.hostname,
  //         });
  //       } else if (host.type == "prediction") {
  //         tempNodes.push({
  //           id: host.entityIp + "_" + (host.type || "unknown"),
  //           altname: host.entityIp,
  //           type: host.entityType,
  //           mac: "",
  //           group: 1,
  //           level: 3,
  //           hostThreatScore: 0,
  //           conntype: host.type,
  //           path: host.path,
  //           allowed_app: host.allowed_app,
  //         });
  //         tempLinks.push({
  //           source: host.subnet,
  //           target: host.entityIp + "_" + (host.type || "unknown"),
  //           conntype: host.type,
  //         });
  //       } else if (host.type == "vulnerability") {
  //         tempNodes.push({
  //           id: host.entityIp + "_" + (host.type || "unknown"),
  //           altname: host.entityIp,
  //           type: host.entityType,
  //           mac: "",
  //           group: 1,
  //           level: 3,
  //           hostThreatScore: 0,
  //           conntype: host.type,
  //           app_version: host.app_version,
  //           vul_id_list: host.vul_id_list,
  //         });
  //         tempLinks.push({
  //           source: host.subnet,
  //           target: host.entityIp + "_" + (host.type || "unknown"),
  //           conntype: host.type,
  //         });
  //       } else {
  //         if (host.hostname != "" && host.hostname != null) {
  //           tempNodes.push({
  //             id: host.hostname + "_" + (host.type || "unknown"),
  //             altname: host.hostname,
  //             hostname: host.hostname,
  //             ip: host.entityIp,
  //             type: host.entityType ? host.entityType : "Computer",
  //             mac: host.mac,
  //             group: 1,
  //             level: 3,
  //             hostThreatScore: host.hostThreatScore,
  //             conntype: host.type,
  //             subnet: host.subnet,
  //           });
  //         } else {
  //           tempNodes.push({
  //             id: host.entityIp + "_" + (host.type || "unknown"),
  //             altname: host.entityIp,
  //             hostname: "",
  //             ip: host.entityIp,
  //             type: host.entityType ? host.entityType : "Computer",
  //             mac: host.mac,
  //             group: 1,
  //             level: 3,
  //             hostThreatScore: host.hostThreatScore,
  //             conntype: host.type,
  //             subnet: host.subnet,
  //           });
  //         }
  //         tempLinks.push({
  //           source: host.subnet,
  //           target:
  //             (host.hostname != "" ? host.hostname : host.entityIp) +
  //             "_" +
  //             (host.type || "unknown"),
  //           conntype: host.type,
  //         });
  //       }
  //     });
  //     if (subnet) {
  //       setNodes((prev: any) => [...prev, ...tempNodes]);
  //       setLinks((prev: any) => [...prev, ...tempLinks]);
  //     } else {
  //       setNodes(tempNodes);
  //       setLinks(tempLinks);
  //     }
  //   }
  // }, [connectionData]);

  useEffect(() => {
    if (subnet == null) return;
    refetchConnectionData();
  }, [subnet]);

  const [actionData, setActionData] = useState<any>({});
  function takeAction(e: number, action: string) {
    actionData[action] = e;
    setActionData(actionData);
    let _name;
    switch (action) {
      case "watched":
        _name = "is_watched";
        break;
      case "whitelistMac":
        _name = "whitelist_mac";
        break;
      case "whitelistIp":
        _name = "whitelist_ip";
        break;
      case "critical":
        _name = "is_critical";
        break;
    }

    // const data: any = {
    //   macAddress: mac,
    //   userName: "lsadmin",
    //   isAdmin: 0,
    // };

    const data: any = [
      {
        macAddress: mac,
        attributes: [
          {
            name: _name,
            isCustom: false,
            value: e,
            metadata: {
              time_seen: Math.floor(Date.now() / 1000),
              source: "user",
              sub_source: "EntityInspector",
            }, // as discussed with shafraz adding metadata for the action
          },
        ],
        modeChange: "replace",
      },
    ];

    // data[action] = e;

    watchAction.mutate(data);
  }

  // const [ExternalLogsData, setExternalLogsData] = useState<any>([]);

  // const {
  //   data: entityExternalLogs,
  //   refetch: refetchDenyLogs,
  //   // isLoading: isLoadingExternalLogs,
  //   // isFetching: isFetchingExternalLogs,
  // } = useQuery(
  //   ["getEntityExternalLogs"],
  //   ({ signal }) =>
  //     getEntityExternalLogs(
  //       {
  //         macAddress: mac,
  //         ipAddress: ipAddress,
  //         filterTime: filterTime,
  //         type: mac ? "mac" : "ip",
  //       },
  //       signal,
  //     ),
  //   {
  //     enabled: false,
  //   },
  // );

  // useEffect(() => {
  //   setTimeout(() => {
  //     refetchDenyLogs();
  //   }, 3000);
  // }, []);

  // useEffect(() => {
  //   if (entityExternalLogs) {
  //     //update dataset with proper fields
  //     // let logSources = entityExternalLogs.deviceLogs.map((log: any) => {
  //     //   return log.product_type;
  //     // });
  //     // logSources = [...new Set(logSources)];

  //     // if (logSources.length > 0) {
  //     //   const s360Urltemp = encodeURI(
  //     //     `shadow360/?date={"endDate":"${"1440"}","startDate":"${"1440"}"}&plainText={"textFieldValue":"${entityData?.ip_addr || entityData?.ip}","textLogType":${JSON.stringify(logSources)},"textType":"username", "aId":""}&smartSearch=0&query={"textFieldValue":"${entityData?.ip_addr || entityData?.ip}","textLogType":${JSON.stringify(logSources)},"textType":"username", "aId":""}`,
  //     //   );
  //     //   setS360Url(s360Urltemp);
  //     // }
  //     const temp = entityExternalLogs.deviceLogs.map((log: any) => {
  //       log["rawlog"] = (
  //         <div className="text-center">
  //           <Search
  //             size={14}
  //             className="text-primary cursor-pointer"
  //             onClick={() => setAssetRawLog(log.raw_message || "")}
  //           />
  //         </div>
  //       );
  //       return log;
  //     });
  //     setExternalLogsData(temp || []);
  //   }
  // }, [entityExternalLogs, entityData]);

  const handleEntityAttribute = () => {
    setEntityTimeSeriesData({
      system: [],
      custom: [],
    });
    setEntityAttributeDialog(true);
    getEntityTimeSeriesAttribute.mutate({
      macAddress: mac,
      pastHours: convertToHours(filterTime),
    });
  };

  const anomaliesCol = [
    { header: "Category", accessor: "category", type: "component" },
    { header: "Type", accessor: "type" },
    { header: "Description", accessor: "description", type: "component" },
    { header: "Events", accessor: "events", type: "component" },
    { header: "Score", accessor: "score", type: "component" },
    { header: "Time", accessor: "time", type: "component" },
    { header: "Sensor Name", accessor: "sensor" },
  ];

  // const alertsData: any[] = [];
  const alertsCol = [
    { header: "Category", accessor: "category", type: "component" },
    { header: "Type", accessor: "type" },
    // { header: "Remote IP / MAC", accessor: "remoteIpMac" },
    { header: "Description", accessor: "desc" },
    { header: "Events", accessor: "events", type: "component" },
    { header: "Time Seen", accessor: "time" },
    { header: "Score", accessor: "score", type: "component" },
    { header: "Sensor Name", accessor: "sensor" },
  ];

  const outdatedSoftwaresCol = [
    { header: "App", accessor: "app" },
    { header: "Version", accessor: "version" },
  ];

  const vulnerabilitiesCol = [
    { header: "Type", accessor: "type" },
    { header: "Name", accessor: "name" },
    { header: "Version", accessor: "version" },
    { header: "CVE", accessor: "cve" },
    { header: "Description", accessor: "description" },
    { header: "Severity", accessor: "severity" },
    { header: "Impact Score", accessor: "impactScore", type: "component" },
    { header: "Base Score", accessor: "baseScore", type: "component" },
  ];

  // const externalLogsCol = [
  //   { header: "Device Type", accessor: "product_type" },
  //   { header: "Log Source", accessor: "log_source_ip" },
  //   { header: "Source IP", accessor: "source_ip" },
  //   { header: "Remote IP", accessor: "destination_ip" },
  //   { header: "Remote Port", accessor: "destination_port" },
  //   { header: "Country", accessor: "country" },
  //   { header: "Direction", accessor: "direction" },
  //   { header: "Time", accessor: "datetime" },
  //   // { header: "", accessor: "rawlog", type: "component" },
  // ];

  const userAccessCol = [
    { header: "First Activity", accessor: "first_login" },
    { header: "Last Activity", accessor: "last_login" },
    { header: "Username", accessor: "username" },
    { header: "Assigned IP", accessor: "ip" },
  ];

  const dhcpSessionsCol = [
    { header: "First Seen", accessor: "firstSeen" },
    { header: "Last Updated Time", accessor: "lastUpdatedTime" },
    { header: "Assigned IP", accessor: "assignedIp" },
    { header: "Source", accessor: "source" },
  ];

  const servicesCol = [{ header: "Service", accessor: "service" }];

  const inventoryCol = [
    { header: "App", accessor: "app" },
    { header: "Versions", accessor: "versions" },
  ];

  const networkUsersCol = [
    { header: "Username", accessor: "username" },
    { header: "Last Seen", accessor: "lastSeen" },
    { header: "Source", accessor: "source" },
  ];

  function timeChanged(time: string) {
    setfilterTime(time);
  }

  useEffect(() => {
    // refetchConnectionData();
    refetchUserSessions();
    // refetchVulnerability();
    refetchNonADUsers();
    // refetchStats();
    refetchEntityData();
    refetchEntityFlowSummary();
  }, [filterTime]);

  const [connectionExpand, setConnectionExpand] = useState(false);

  const logtype = encodeURI('["entity_alerts", "entity_anomalies"]');
  const s360StagedDataUrlPDF = encodeURI(
    `${process.env.API_URL}/downloadPdf/?date=${filterTime}&plainText={"textFieldValue":"${summaryData["IP"]?.name}","textLogType":${logtype},"textTabType":"shadow360-stages","textType":"ip", "aId":""}&smartSearch=0&query={"textFieldValue":"${summaryData["IP"]?.name}","textLogType":"","textType":"ip", "aId":""}&hostname="${summaryData["Hostname"]?.name}"&mac=${summaryData["MAC"]?.name}`,
  );

  const plainTextQuery = JSON.stringify({
    textFieldValue: summaryData["IP"]?.name,
    textLogType: ["entity_alerts", "entity_anomalies"],
    textTabType: "shadow360-stages",
    textType: "ip",
    aId: "",
  });

  const timeInMin: { [key: string]: number } = {
    day: 1440,
    "3day": 4320,
    "7day": 10080,
    month: 43200,
    "2month": 86400,
  };

  const s360StagedDataUrl = `/shadow360/?date={"endDate":"${timeInMin[filterTime]}","startDate":"${timeInMin[filterTime]}"}&plainText=${plainTextQuery}&smartSearch=0&query={"textFieldValue":"${summaryData["IP"]?.name}","textLogType":"","textType":"ip", "aId":""}`;

  function createCirclePath(score: number) {
    if (score > 99) {
      score = 99;
    }
    // alert(score)
    const angle = (score / 100) * 360;
    const radius = 30 * scale;
    const centerX = 48 * scale;
    const centerY = 48 * scale;

    const startX = centerX + radius;
    const startY = centerY;

    const endX = centerX + radius * Math.cos((2 * Math.PI * angle) / 360);
    const endY = centerY + radius * Math.sin((2 * Math.PI * angle) / 360);

    const largeArcFlag = angle <= 180 ? 0 : 1;

    const pathData = `
        M ${startX},${startY}
        A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY}
    `;

    return pathData;
  }

  const dummyPath = createCirclePath(99.999);
  const threatScorePath = createCirclePath(threatScore);
  const thirdPartyScorePath = createCirclePath(thirdPartyScore);

  // const classDrop = [
  //   "AV Server",
  //   "DHCP Server",
  //   "DNS Server",
  //   "File Server",
  //   "Firewall",
  //   "FTP Server",
  //   "HADOOP",
  //   "HYPER-V",
  //   "IP Phones",
  //   "LDAP Server",
  //   "Linux Server",
  //   "Mail Server",
  //   "Mobile",
  //   "MongoDB",
  //   "MSSQL Database",
  //   "MySQL DB",
  //   "Network Access Control",
  //   "Network Security Appliance",
  //   "ORACLE Database",
  //   "Postgres DB",
  //   "Print Server / Printer",
  //   "Proxy Server",
  //   "RDP",
  //   "SCCM Server",
  //   "SCOM Server",
  //   "Security Device",
  //   "Virtual Machine",
  //   "Vulnerability Scanner",
  //   "Web Server",
  //   "Windows Server",
  //   "Windows Workstation",
  // ];

  // const editable: any = {
  //   Hostname: { id: "hostname" },
  //   Category: { id: "category" },
  //   OS: { id: "operating_system" },
  //   "Endpoint Security": { id: "endpoint_security" },
  //   Department: { id: "department" },
  //   Tags: { id: "tags" },
  //   Classification: { id: "classification" },
  //   Comments: { id: "comments" },
  // };
  // const editable: any = {
  //   Hostname: { id: "popuphostEntityHostname" },
  //   Category: { id: "popuphostCategory" },
  //   OS: { id: "popuphostOs" },
  //   "Endpoint Security": { id: "popuphostEndPointSecurity" },
  //   Department: { id: "popuphostDepartment" },
  //   Tags: { id: "popuphostTags" },
  //   Classification: { id: "popuphostClassification" },
  //   Comments: { id: "popuphostAssetComments" },
  // };

  // const [newTags, setNewTags] = useState<string[]>([]);

  const [openDomainList, setOpenDomainList] = useState<boolean>(false);

  const searchList = () => {
    setOpenDomainList(true);
  };

  let defTab = defaultTab;
  if (isFeatureAllowed(ALL_KEYS.ASSET_ENTITY_INSPECTOR_ANOMALIES) == false) {
    defTab = "dhcpSessions";
  }

  const entityInspectorRef = useRef<HTMLDivElement | null>(null);

  const assetAttributeCols = [
    { header: "Attribute", accessor: "attribute", type: "component" },
    { header: "Value", accessor: "value", type: "component" },
  ];

  const relatedMacCols = [
    { header: "Related MAC", accessor: "relatedMac", type: "component" },
  ];

  const downloadConnections = () => {
    const data = {
      macAddress: mac,
      ipAddress: ipAddress,
      subnet: subnet || "",
      filterTime: filterTime,
      type: mac ? "mac" : "ip",
    };

    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(data)
          .filter(
            ([, value]) =>
              value !== undefined && value !== null && value !== "",
          )
          .map(([key, value]) => [
            key,
            Array.isArray(value)
              ? value.join(",")
              : typeof value === "boolean"
                ? value
                  ? "true"
                  : "false"
                : value
                  ? value.toString()
                  : "",
          ]),
      ),
    ).toString();

    const connectionUrl = `${process.env.API_URL}/fastapi/entity/download-entity-connections?${queryString}`;
    window.open(connectionUrl, "_blank");
  };

  // const connectionUrl =
  //   "/download-entity-connections/?entity=" +
  //   mac +
  //   "&time=" +
  //   filterTime +
  //   "&type=all";

  const connectionOptions = (
    <div className="flex items-center gap-[1px] me-[1px]">
      <div
        className="p-2 border-background bg-secondary cursor-pointer"
        // onClick={() => window.open(connectionUrl, "_blank")}
        onClick={downloadConnections}
      >
        <DownloadIcon size={14} />
      </div>
      <div
        className="p-2 border-background bg-secondary cursor-pointer"
        onClick={() => setConnectionExpand(!connectionExpand)}
      >
        {connectionExpand ? <Minimize size={14} /> : <ExpandIcon size={14} />}
      </div>
    </div>
  );

  // const externalLogShadowBtn = (
  //   <div>
  //     <Button
  //       variant="outline"
  //       className="bg-primary text-white"
  //       onClick={() => {
  //         if (s360Url == "") {
  //           toast({
  //             title: "Error",
  //             description: "No logs found for this entity in Shadow 360",
  //             variant: "destructive",
  //           });
  //           return;
  //         }
  //         window.open(s360Url, "_blank");
  //       }}
  //     >
  //       View in Shadow360
  //     </Button>
  //   </div>
  // );

  const deleteEntity = () => {
    const data = {
      id: entityData?.id,
      macAddress: mac,
    };
    deleteAssetMutation.mutate(data);
    setDeleting(false);
  };

  const deleteHandler = () => {
    if (!mac) return;
    setDeleting(true);
  };

  const handleExternalLogsClick = () => {
    const classificationsFieldValues = [
      "recon",
      "attack",
      "malware",
      "exploit",
      "system/privilege abuse",
      "suspicious",
      "traffic denied",
      "error",
      "authentication failure",
      "audit failure",
      // "vulnerability",
      // "warning",
      // "critical",
      // "alert"
    ];

    const S360_LINK = getShadow360QueryBuilderURL({
      startTime: convertToEpochTime(filterTime),
      endTime: Math.floor(Date.now() / 1000),
      logSrcs: ["firewall"],
      condition: "AND",
      fieldValObj: classificationsFieldValues.map((classification) => ({
        fieldName: "classification",
        fieldValue: classification,
      })),
      sensor: "master",
      operation: "in_reverse",
    });
    window.open(S360_LINK, "_blank");
  };

  return (
    <>
      {/* External log raw data */}
      {assetRawLog && (
        <Dialog open={true} onOpenChange={() => setAssetRawLog("")}>
          <DialogContent className="sm:max-w-[60vw]">
            <DialogHeader>
              <DialogTitle>Raw Log</DialogTitle>
            </DialogHeader>
            <div className="text-sm">{assetRawLog}</div>
          </DialogContent>
        </Dialog>
      )}

      <ImportParserModal
        importParserOpen={assetEntityAttributes}
        onClose={() => setAssetEntityAttributes(false)}
        data={attributeModalData}
      />
      {/* External log raw data */}

      {userId != "" ? (
        <UserInvestigator
          account={userId}
          openState={true}
          onClose={() => setUserId("")}
        />
      ) : (
        ""
      )}

      {deleting && (
        <ConfirmDialog
          text="Are you sure you want to delete this entity?"
          confirmAction={deleteEntity}
          rejectAction={() => setDeleting(false)}
          show={deleting}
          closeDialog={() => setDeleting(false)}
        />
      )}

      <Dialog
        open={entityAttributeDialog}
        onOpenChange={() => setEntityAttributeDialog(false)}
      >
        <DialogContent className="sm:max-w-[45vw]">
          <DialogHeader>
            <DialogTitle>Asset Attributes</DialogTitle>
          </DialogHeader>
          <div className="max-h-[80vh] w-full overflow-y-auto">
            <DataTable
              cols={assetAttributeCols}
              data={
                attributeTab == "default"
                  ? defaultEntityAttributeRows
                  : customEntityAttributeRows
              }
              colFilter={false}
              countPerPageSelection={true}
              pagination={true}
              totalCount={
                attributeTab == "default"
                  ? defaultEntityAttributeRows.length
                  : customEntityAttributeRows.length
              }
              noSortColumns={["action"]}
            />
          </div>
        </DialogContent>
      </Dialog>

      {Object.keys(eventProps).length > 0 && (
        <EventData
          apiProps={eventProps}
          isOpen={true}
          onClose={() => setEventProps({})}
        />
      )}

      <Dialog
        open={openDomainList}
        onOpenChange={() => setOpenDomainList(false)}
      >
        <DialogContent className="w-[80%]">
          <DialogHeader>
            <DialogTitle>Top Queried Domains</DialogTitle>
          </DialogHeader>
          <DataTable
            data={
              statsData?.domain_count?.map((e: any) => {
                return {
                  domain: e.name,
                  count: e.value,
                };
              }) || []
            }
            cols={[
              { header: "Domain", accessor: "domain" },
              { header: "Count", accessor: "count" },
            ]}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={Object.keys(summaryEditData).length > 0}
        onOpenChange={() => setSummaryEditData({})}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{summaryEditData.title}</DialogTitle>
          </DialogHeader>
          <div className="w-full">
            {summaryEditData.fieldType == "string" && (
              <div>
                <Input
                  onChange={(e) => {
                    summaryEditData["newvalue"] = e.target.value;
                  }}
                  type="text"
                  placeholder={summaryEditData.title + "..."}
                  defaultValue={
                    summaryEditData.defaultValue || summaryEditData.name
                  }
                />
              </div>
            )}
            {summaryEditData.fieldType == "dropdown" && (
              <div>
                <CustomSelect
                  defaultValue={summaryEditData.newvalue}
                  data={summaryEditData.fieldOptions}
                  placeholder={summaryEditData.title + "..."}
                  onSelect={(e: any) => {
                    summaryEditData["newvalue"] = e;
                    setSummaryEditData({ ...summaryEditData });
                  }}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveEntityData} type="submit">
              <span>Save</span>
              <LoaderIcon
                size={14}
                className={
                  "ms-2 animate-spin " + (savingSummary ? "" : "hidden")
                }
              />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          style={{
            width:
              getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR).width || "90vw",
            marginLeft:
              getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR).marginLeft ||
              "-45vw",
            display: "flex",
            flexDirection: "column",
          }}
          className={`h-[90vh] p-2 max-w-none overflow-y-auto overflow-x-hidden transform-none -mt-[45vh]`}
        >
          {isFeatureAllowed(ALL_KEYS.SHADOW_GPT_FLOAT_ASSET_DISCOVERY) && (
            <ShadowGPTFloat
              data={{
                page: "__entityinspector__",
                macAddress: mac,
                ipAddress: ipAddress,
              }}
              displayIn="dialog"
            />
          )}
          {(entityLoading || refetchingEntity || fetchingEntityFlowSummary) &&
            !entityError && <DialogLoader show={true} />}
          {entityError ? (
            <div className="absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4 text-xl">
              Error Loading Entity Detail
            </div>
          ) : (
            <div ref={entityInspectorRef}>
              <DialogHeader>
                <div className="grid grid-cols-2 my-2">
                  <div className="col-span-1 pt-2">
                    <DialogTitle>Entity Inspector</DialogTitle>
                  </div>
                  <div className="col-span-1 text-end flex justify-end items-center gap-2">
                    <a
                      href={`/download-entity-inspector-pdf/?entity=${mac ? mac : ipAddress}&type=${mac ? "mac" : "ip"}&time=${filterTime}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Entity Inspector Download"
                    >
                      <FileDown className="w-6 h-6 me-2 cursor-pointer" />
                    </a>
                    <TimeFilter
                      onChangeEvent={(e: string) => timeChanged(e)}
                      defaultValue={filter_time}
                    />
                  </div>
                </div>
              </DialogHeader>

              <div className="relative">
                <div className="grid grid-cols-12 gap-2">
                  <div
                    className={
                      getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR_SUMMARY)
                        .class ||
                      (nonMacAssetRef.current ? "col-span-3" : "col-span-4")
                    }
                  >
                    <CustomWidget
                      customOptions={
                        nonMacAssetRef.current || !isAdminUser ? null : (
                          <div
                            className={`p-2 border-background bg-secondary cursor-pointer`}
                            onClick={deleteHandler}
                          >
                            <Trash size={14} />
                          </div>
                        )
                      }
                      onEditClick={
                        nonMacAssetRef.current
                          ? undefined
                          : () => setEditing(!editing)
                      }
                      title="Summary"
                      className="h-96"
                      info="Provides summary details of some important information related to that particular asset"
                    >
                      <div id="" className="w-full h-72 text-xs leading-5 mt-2">
                        {entityLoading ? (
                          <TableLoader />
                        ) : (
                          <div className="w-full h-full">
                            <div className="w-2/3 pe-2 border-r inline-block h-72 overflow-y-auto">
                              {Object.keys(summaryData).map(
                                (key: string, indxx: number) => (
                                  <div
                                    className="py-2 text-xs border-b rounded-sm grid grid-cols-10 gap-2 w-full"
                                    key={indxx}
                                  >
                                    <div className="col-span-4 relative">
                                      <span>{key}</span>
                                      {"keyTooltip" in summaryData[key] ? (
                                        <div className="inline-block -mb-1">
                                          <CustomTooltip
                                            text={summaryData[key].keyTooltip}
                                            side="right"
                                          >
                                            <InfoIcon
                                              size={12}
                                              className="ms-2 text-primary"
                                            />
                                          </CustomTooltip>
                                        </div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                    <div
                                      className={`col-span-6 ${
                                        key === "Related MAC"
                                          ? "whitespace-normal break-words"
                                          : "overflow-hidden whitespace-nowrap"
                                      }`}
                                    >
                                      <div
                                        onClick={() => {
                                          if (
                                            editing &&
                                            summaryData[key].editable
                                          ) {
                                            setSummaryEditData({
                                              ...summaryData[key],
                                              newvalue: summaryData[key].name,
                                            });
                                          }
                                        }}
                                        className={`${
                                          key === "Related MAC"
                                            ? "block break-words"
                                            : "inline-block"
                                        } ${editing && summaryData[key].editable ? "text-blue-500 cursor-pointer" : ""}`}
                                      >
                                        {summaryData[key].name ||
                                          (editing && summaryData[key].editable
                                            ? "Not Available"
                                            : "- - -")}
                                      </div>
                                      {"toolTip" in summaryData[key] ? (
                                        <div className="inline-block -mb-1">
                                          <CustomTooltip
                                            text={summaryData[key].toolTip}
                                            side="right"
                                          >
                                            <InfoIcon
                                              size={12}
                                              className="ms-2 text-primary"
                                            />
                                          </CustomTooltip>
                                        </div>
                                      ) : (
                                        ""
                                      )}
                                    </div>
                                  </div>
                                ),
                              )}
                            </div>
                            <div className="w-1/3 h-full align-top inline-block text-center relative overflow-y-hidden">
                              <div className="h-full w-full flex flex-col">
                                <div className="border-b mx-2 flex-1 flex flex-col justify-center items-center">
                                  <svg className="-rotate-90 h-24 w-24 inline-block">
                                    <path
                                      d={dummyPath}
                                      fill="none"
                                      stroke="var(--secondary)"
                                      strokeWidth={6 * scale}
                                      strokeLinecap="round"
                                    />
                                    <path
                                      d={threatScorePath}
                                      fill="none"
                                      stroke="var(--primary)"
                                      strokeWidth={6 * scale}
                                      strokeLinecap="round"
                                    />
                                    <text
                                      x={48 * scale}
                                      y={-42 * scale}
                                      style={{
                                        textAnchor: "middle",
                                        fontSize: "1rem",
                                      }}
                                      transform="rotate(90)"
                                      fill="var(--foreground)"
                                    >
                                      {threatScore}
                                    </text>
                                  </svg>
                                  <div className="font-bold">
                                    {getFeatureValues(
                                      ALL_KEYS.ASSET_ENTITY_INSPECTOR_THREATSCORE,
                                    ).label || "Threat Score"}
                                  </div>
                                </div>
                                {isFeatureAllowed(
                                  ALL_KEYS.ASSET_ENTITY_INSPECTOR_THIRD_SCORE,
                                ) && (
                                  <div className="mx-2 flex-1 flex flex-col justify-center items-center">
                                    <svg className="-rotate-90 h-24 w-24 inline-block">
                                      <path
                                        d={dummyPath}
                                        fill="none"
                                        stroke="var(--secondary)"
                                        strokeWidth={6 * scale}
                                        strokeLinecap="round"
                                      />
                                      <path
                                        d={thirdPartyScorePath}
                                        fill="none"
                                        stroke="var(--primary)"
                                        strokeWidth={6 * scale}
                                        strokeLinecap="round"
                                      />
                                      <text
                                        x={48 * scale}
                                        y={-42 * scale}
                                        style={{
                                          textAnchor: "middle",
                                          fontSize: "1rem",
                                        }}
                                        transform="rotate(90)"
                                        fill="var(--foreground)"
                                      >
                                        {thirdPartyScore}
                                      </text>
                                    </svg>
                                    <div className="font-bold">
                                      Third Party Detection Score
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CustomWidget>
                  </div>
                  {!nonMacAssetRef.current && (
                    <>
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_ACTIONS,
                      ) && (
                        <div
                          className={`${getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR_ACTIONS).class || "col-span-2"}`}
                        >
                          <CustomWidget
                            title="Actions"
                            className="h-96"
                            info="List of actions that can be taken against this entity on LinkShadow's console"
                          >
                            <div className="w-full h-80 mt-2 flex flex-col">
                              <div className="flex-1 flex gap-2 flex-col">
                                <div className="flex items-center space-x-2">
                                  <Switch
                                    id="watchlist"
                                    checked={hostDetailData.is_watched}
                                    className="shadow-md border border-border-secondary"
                                    onCheckedChange={(e: boolean) =>
                                      takeAction(e ? 1 : 0, "watched")
                                    }
                                  />
                                  <Label htmlFor="watchlist">Watchlist</Label>
                                </div>
                                <div className="items-center space-x-2 flex">
                                  <Switch
                                    id="whitelistmac"
                                    checked={
                                      parseInt(hostDetailData.whitelist_mac) ==
                                      1
                                    }
                                    className="shadow-md border border-border-secondary"
                                    onCheckedChange={(e) =>
                                      takeAction(e ? 1 : 0, "whitelistMac")
                                    }
                                  />
                                  <Label htmlFor="whitelistmac">
                                    Whitelist MAC
                                  </Label>
                                </div>
                                <div className="items-center space-x-2 flex">
                                  <Switch
                                    id="watchlistip"
                                    className="shadow-md border border-border-secondary"
                                    checked={
                                      parseInt(hostDetailData.whitelist_ip) == 1
                                    }
                                    onCheckedChange={(e) =>
                                      takeAction(e ? 1 : 0, "whitelistIp")
                                    }
                                  />
                                  <Label htmlFor="watchlistip">
                                    Whitelist IP
                                  </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Switch
                                    id="critical"
                                    className="shadow-md border border-border-secondary"
                                    checked={hostDetailData.is_critical}
                                    onCheckedChange={(e) =>
                                      takeAction(e ? 1 : 0, "critical")
                                    }
                                  />
                                  <Label htmlFor="critical">Critical</Label>
                                </div>
                              </div>

                              <div className="w-full grid grid-cols-2 py-4 border-t flex-1">
                                <div className="col-span-1 border-r text-xs">
                                  <a
                                    href={s360StagedDataUrlPDF}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <div className="flex justify-center items-center gap-2 flex-col">
                                      <div className="rounded-full p-1 border-2 border-primary shadow">
                                        <img
                                          src={
                                            process.env.STATIC_PATH +
                                            "/images/menu-icons/s360.png"
                                          }
                                          className="h-8 w-8"
                                          alt=""
                                          style={{
                                            filter:
                                              theme == "light"
                                                ? "invert(1)"
                                                : "invert(0)",
                                          }}
                                        />
                                      </div>
                                      <div className="mt-2 text-center">
                                        Asset Risk Summary (Download)
                                      </div>
                                    </div>
                                  </a>
                                </div>
                                <div className="col-span-1 text-xs">
                                  <Link
                                    to={s360StagedDataUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <div className="flex justify-center items-center gap-2 flex-col">
                                      <div className="rounded-full p-1 border-2 border-primary shadow">
                                        <img
                                          src={
                                            process.env.STATIC_PATH +
                                            "/images/menu-icons/s360.png"
                                          }
                                          className="w-8 h-8"
                                          alt=""
                                          style={{
                                            filter:
                                              theme == "light"
                                                ? "invert(1)"
                                                : "invert(0)",
                                          }}
                                        />
                                      </div>
                                      <div className="mt-2 text-center">
                                        Asset Risk Summary (View)
                                      </div>
                                    </div>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </CustomWidget>
                        </div>
                      )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_USER,
                      ) && (
                        <div
                          className={
                            getFeatureValues(
                              ALL_KEYS.ASSET_ENTITY_INSPECTOR_USER,
                            ).class || "col-span-2"
                          }
                        >
                          <CustomWidget
                            title="User"
                            className="h-96"
                            info="List of users who have access to this entity"
                          >
                            <div
                              id=""
                              className="w-full mt-2 relative overflow-y-auto h-72"
                            >
                              {entityLoading ? (
                                <TableLoader colCount={1} />
                              ) : entityData && entityData?.user.length > 0 ? (
                                entityData?.user.map(
                                  (user: any, index: number) => {
                                    return (
                                      <div
                                        key={index}
                                        onClick={() => setUserId(user.username)}
                                        className="cursor-pointer text-sm mb-2"
                                      >
                                        {user.display_name || user.username}
                                      </div>
                                    );
                                  },
                                )
                              ) : (
                                <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                                  <NoData />
                                </div>
                              )}
                            </div>
                          </CustomWidget>
                        </div>
                      )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_CONN,
                      ) && (
                        <div
                          className={
                            connectionExpand ? "col-span-12" : "col-span-4"
                          }
                        >
                          <CustomWidget
                            className={`relative ${connectionExpand ? "h-[80vh]" : "h-96"}`}
                            title="Connections"
                            customOptions={connectionOptions}
                            info="A connection graph showing the communication path between assets, users, internet and/or critical assets"
                          >
                            <div
                              id="connectionChart"
                              className={
                                "w-full mt-2 relative " +
                                (connectionExpand ? "h-[70vh]" : "h-72")
                              }
                            >
                              {entityFlowSummaryLoading ||
                              fetchingEntityFlowSummary ||
                              connectionFetching ||
                              connectionLoading ? (
                                <ConnectionLoader variant="small" />
                              ) : nodes.length > 0 ? (
                                <ConnectionChart
                                  parentid="connectionChart"
                                  linkLength={100}
                                  data={{ nodes: nodes, links: links }}
                                  onNodeClick={(e: string) => {
                                    if (subnet == e) {
                                      setSubnet("");
                                    } else {
                                      setSubnet(e);
                                    }
                                  }}
                                  maxNodes={30}
                                />
                              ) : (
                                <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                                  <NoData />
                                </div>
                              )}
                            </div>
                          </CustomWidget>
                        </div>
                      )}
                    </>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_GEO_COMM,
                  ) && (
                    <div
                      className={
                        nonMacAssetRef.current ? "col-span-3" : "col-span-4"
                      }
                    >
                      <CustomWidget
                        title="GEO Communications"
                        info="Entity's geographical communication pattern"
                      >
                        <div
                          id="entityGeoCommunicationsChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <DonutChartLoader />
                          ) : statsData &&
                            statsData?.country_count?.length > 0 ? (
                            <DonutChart
                              parentid="entityGeoCommunicationsChart"
                              data={statsData?.country_count || []}
                              valuetype="count"
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_TOP_DOMAIN_CAT,
                  ) && (
                    <div
                      className={
                        nonMacAssetRef.current ? "col-span-3" : "col-span-4"
                      }
                    >
                      <CustomWidget
                        title="Top Domain Categories"
                        info="Top Domain Categories"
                      >
                        <div
                          id="entityTopDomainCat"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <DonutChartLoader />
                          ) : statsData &&
                            statsData?.domain_category_count?.length > 0 ? (
                            <DonutChart
                              parentid="entityTopDomainCat"
                              data={statsData?.domain_category_count || []}
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_TOP_QUERIED_DOMAINS,
                  ) && (
                    <div
                      className={
                        nonMacAssetRef.current ? "col-span-3" : "col-span-4"
                      }
                    >
                      <CustomWidget
                        title="Top Queried Domains"
                        info="Top domains seen queried by this entity, allowing you to further investigate all these queries, and the number of times it's been visited for each domain"
                        onDetailClick={searchList}
                        detailButton={
                          statsData && statsData?.domain_count?.length > 0
                            ? true
                            : false
                        }
                      >
                        <div
                          id="entityTopQueriedDomainsChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <BarChartLoader />
                          ) : statsData &&
                            statsData?.domain_count?.length > 0 ? (
                            <BarChart
                              parentid="entityTopQueriedDomainsChart"
                              data={
                                statsData?.domain_count?.map((e: any) => {
                                  return {
                                    label: e.name,
                                    value: e.value,
                                  };
                                }) || []
                              }
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_TOP_BANDWIDTH,
                  ) && (
                    <div className="col-span-3">
                      <CustomWidget
                        title="Top Services by Bandwidth"
                        info="Top 5 services seen being used by this asset, by the bandwidth amount transferred"
                      >
                        <div
                          id="topServicesByBandwidthChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <BarChartLoader orientation="vertical" />
                          ) : statsData &&
                            statsData?.protocol_bandwidth?.length > 0 ? (
                            <VerticalBarChart
                              parentid="topServicesByBandwidthChart"
                              data={topServicesBandwidthData}
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_TOP_NOISY,
                  ) && (
                    <div className="col-span-3">
                      <CustomWidget
                        title="Top Noisy Services"
                        info="Top noisy services based on event count for each service"
                      >
                        <div
                          id="topNoisyServicesChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <BarChartLoader orientation="vertical" />
                          ) : statsData &&
                            statsData?.protocol_count?.length > 0 ? (
                            <VerticalBarChart
                              parentid="topNoisyServicesChart"
                              data={topNoisyServicesData}
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_PORT_PROT,
                  ) && (
                    <div className="col-span-3">
                      <CustomWidget
                        title="Port-Protocol"
                        info="Summary view on which protocol is being consumed and which on which port it's being used"
                      >
                        <div
                          id="portProtocolChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <DonutChartLoader />
                          ) : statsData &&
                            (statsData?.protocolMatrix?.protocols.length > 0 ||
                              statsData?.protocolMatrix?.ports.length > 0 ||
                              statsData?.protocolMatrix?.matrix.length > 0) ? (
                            <ChordDiagram
                              parentid="portProtocolChart"
                              protocolMatrixData={statsData?.protocolMatrix}
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_ASSOCIATE_PORT,
                  ) && (
                    <div className="col-span-3">
                      <CustomWidget
                        title="Associated Ports"
                        info="List of open ports seen on this asset"
                      >
                        <div
                          id="portSummaryChart"
                          className="w-full h-80 relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <DonutChartLoader />
                          ) : statsData &&
                            statsData?.port_summary?.length > 0 ? (
                            <SankeyGraph
                              parentid="portSummaryChart"
                              portSummaryData={statsData?.port_summary}
                            />
                          ) : (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {isFeatureAllowed(
                    ALL_KEYS.ASSET_ENTITY_INSPECTOR_TRAFFIC_PATTERN,
                  ) && (
                    <div
                      className={
                        nonMacAssetRef.current ? "col-span-12" : "col-span-8"
                      }
                    >
                      <CustomWidget
                        title="Traffic Pattern"
                        className="h-96"
                        info="Pattern of all the traffic types being used by this entity"
                      >
                        <div
                          id="trafficpattern"
                          className="w-full h-full relative"
                        >
                          {entityFlowSummaryLoading ? (
                            <BarChartLoader />
                          ) : trafficPattern.length == 0 ? (
                            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                              <NoData />
                            </div>
                          ) : (
                            <LineChart
                              config={{
                                timeSeries: true,
                                yFormat: "bandwidth",
                                showPoints: true,
                              }}
                              parentid="trafficpattern"
                              data={trafficPattern}
                              labels={trafficLabels}
                            />
                          )}
                        </div>
                      </CustomWidget>
                    </div>
                  )}
                  {!nonMacAssetRef.current &&
                    isFeatureAllowed(
                      ALL_KEYS.ASSET_ENTITY_INSPECTOR_AD_ATTR,
                    ) && (
                      <div className="col-span-4">
                        <CustomWidget
                          onDetailClick={handleEntityAttribute}
                          title="Entity Attributes"
                          detailButton={true}
                          className="h-96 relative"
                          info="Active Directory attributes collected on this entity from your AD"
                        >
                          <Tabs
                            defaultValue="default"
                            onValueChange={(tabname: string) =>
                              setAttributeTab(tabname)
                            }
                          >
                            <TabsList>
                              <TabsTrigger value="default" className="text-xs">
                                Active Directory
                              </TabsTrigger>
                              <TabsTrigger value="custom" className="text-xs">
                                Other
                              </TabsTrigger>
                            </TabsList>
                            <TabsContent
                              value="default"
                              className="text-xs h-full"
                            >
                              <div className="h-60 overflow-y-auto">
                                {entityLoading ? (
                                  <TableLoader />
                                ) : defaultEntityAttributeRows.length == 0 ? (
                                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                    <NoData />
                                  </div>
                                ) : (
                                  <table className="text-xs border-spacing-1 w-full border-collapse">
                                    <tbody>
                                      {defaultEntityAttributeRows
                                        ?.slice(
                                          0,
                                          getFeatureValues(
                                            ALL_KEYS.ASSET_ENTITY_INSPECTOR_AD_ATTR,
                                          ).maxCount || 10,
                                        )
                                        .map((row: any, index: number) => (
                                          <tr
                                            key={index}
                                            className="border-b w-full"
                                          >
                                            <td className="w-[50%] py-2.5">
                                              {row.attribute}
                                            </td>
                                            <td>{row.value}</td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </TabsContent>
                            <TabsContent
                              value="custom"
                              className="text-xs relative"
                            >
                              <div className="h-60 overflow-y-auto">
                                {otherEntityAttributesLoading ? (
                                  <TableLoader />
                                ) : customEntityAttributeRows.length == 0 ? (
                                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                                    <NoData />
                                  </div>
                                ) : (
                                  <table className="text-xs w-full">
                                    {customEntityAttributeRows
                                      .slice(
                                        0,
                                        getFeatureValues(
                                          ALL_KEYS.ASSET_ENTITY_INSPECTOR_AD_ATTR,
                                        ).maxCount || 10,
                                      )
                                      .map((item: any, index: number) => (
                                        <tr
                                          key={index}
                                          className="border-b w-full"
                                        >
                                          <td className="w-[50%] py-2.5">
                                            {item.attribute}
                                          </td>
                                          <td>{item.value}</td>
                                        </tr>
                                      ))}
                                  </table>
                                )}
                              </div>
                            </TabsContent>
                          </Tabs>
                        </CustomWidget>
                      </div>
                    )}
                </div>

                <div className="w-full mt-2">
                  <Tabs defaultValue={defTab} className="w-full">
                    <TabsList className="">
                      {!nonMacAssetRef.current &&
                        mac &&
                        isFeatureAllowed(
                          ALL_KEYS.ASSET_ENTITY_INSPECTOR_ANOMALIES,
                        ) && (
                          <TabsTrigger value="anomalies">
                            <span className="pe-2">Anomalies</span>
                            <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                              {anomaliesData.length}
                            </span>
                            <InfoTooltip
                              text={
                                "Anomaly details collected on this entity in the time range period set"
                              }
                              customClass={"ml-2"}
                            />
                          </TabsTrigger>
                        )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_ALERTS,
                      ) && (
                        <TabsTrigger value="alerts">
                          <span className="pe-2">Alerts</span>
                          <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                            {alertsData.length}
                          </span>
                          <InfoTooltip
                            text={
                              "Alerts seen coming from any assets which have not been profiled"
                            }
                            customClass={"ml-2"}
                          />
                        </TabsTrigger>
                      )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_OUTDATED,
                      ) && (
                        <TabsTrigger value="outdatedSoftwares">
                          <span className="pe-2">Outdated softwares</span>
                          <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                            {outdatedSoftwaresData.length}
                          </span>
                          <InfoTooltip
                            text={
                              "Gives you visibility on all the outdated softwares running on this entity"
                            }
                            customClass={"ml-2"}
                          />
                        </TabsTrigger>
                      )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_VULN,
                      ) && (
                        <TabsTrigger value="vulnerabilities">
                          <span className="pe-2">Vulnerabilities</span>
                          <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                            {vulnerabilitiesData.length}
                          </span>
                          <InfoTooltip
                            text={
                              "Gives you visibility on all the vulnurabilities found on this entity"
                            }
                            customClass={"ml-2"}
                          />
                        </TabsTrigger>
                      )}
                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_EXT_LOGS,
                      ) && (
                        <h4
                          className="inline-flex h-10 items-center justify-center rounded-md px-3 text-sm font-medium transition-all cursor-pointer hover:bg-muted/60"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleExternalLogsClick();
                            }
                          }}
                          onClick={handleExternalLogsClick}
                        >
                          <span>External Logs</span>
                          <ExternalLink size={14} className="ml-1" />
                          <InfoTooltip
                            text={
                              "External logs collected on this entity coming from the SIEM or Syslog sources"
                            }
                            customClass={"ml-2"}
                          />
                        </h4>
                      )}

                      {!nonMacAssetRef.current && (
                        <>
                          {isFeatureAllowed(
                            ALL_KEYS.ASSET_ENTITY_INSPECTOR_USER_ACCESS,
                          ) && (
                            <TabsTrigger value="userAccess">
                              <span className="pe-2">User Access</span>
                              <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                                {userAccessData.length}
                              </span>
                              <InfoTooltip
                                text={
                                  "List of all the users that have access to this entity"
                                }
                                customClass={"ml-2"}
                              />
                            </TabsTrigger>
                          )}

                          <TabsTrigger value="dhcpSessions">
                            <span className="pe-2">DHCP/ARP Sessions</span>
                            <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                              {dhcpSessionsData.length}
                            </span>
                            <InfoTooltip
                              text={
                                "All the DHCP sessions history for IP changes for this specific entity"
                              }
                              customClass={"ml-2"}
                            />
                          </TabsTrigger>

                          {isFeatureAllowed(
                            ALL_KEYS.ASSET_ENTITY_INSPECTOR_SERVICES,
                          ) && (
                            <TabsTrigger value="services">
                              <span className="pe-2">Services</span>
                              <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                                {entityData?.services
                                  ? entityData.services.length
                                  : 0}
                              </span>
                              <InfoTooltip
                                text={
                                  "Any service details captured on this specific entity"
                                }
                                customClass={"ml-2"}
                              />
                            </TabsTrigger>
                          )}
                        </>
                      )}

                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_INVENTORY,
                      ) && (
                        <TabsTrigger value="inventory">
                          <span className="pe-2">Inventory</span>
                          <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                            {inventoryData.length}
                          </span>
                          <InfoTooltip
                            text={"Software user agents detected"}
                            customClass={"ml-2"}
                          />
                        </TabsTrigger>
                      )}

                      {!nonMacAssetRef.current &&
                        isFeatureAllowed(
                          ALL_KEYS.ASSET_ENTITY_INSPECTOR_NET_USERS,
                        ) && (
                          <TabsTrigger value="networkUsers">
                            <span className="pe-2">Network Users</span>
                            <span className="text-xs font-bold py-1 px-2 text-white bg-primary rounded-md">
                              {networkUsersData.length}
                            </span>
                            <InfoTooltip
                              text={
                                "Network authenticated users for this asset"
                              }
                              customClass={"ml-2"}
                            />
                          </TabsTrigger>
                        )}

                      {isFeatureAllowed(
                        ALL_KEYS.ASSET_ENTITY_INSPECTOR_PLUGINS,
                      ) && (
                        <TabsTrigger value="plugins">
                          Plugins
                          <InfoTooltip text={"Plugins"} customClass={"ml-2"} />
                        </TabsTrigger>
                      )}
                    </TabsList>

                    <TabsContent value="anomalies">
                      <div className="w-full grid grid-cols-12 gap-2">
                        <div className="col-span-9">
                          <CustomWidget
                            title="Anomalies"
                            className="h-full"
                            info="Anomaly details collected on this entity in the time range period set"
                          >
                            <div className="w-full h-80" id="anomalyline">
                              <LineChart
                                parentid="anomalyline"
                                data={[anomalyLineData]}
                                config={{ timeSeries: true, showPoints: true }}
                              />
                            </div>
                          </CustomWidget>
                        </div>
                        <div className="col-span-3">
                          <CustomWidget
                            title="Category"
                            info="Entity anomalies for this entity, mapped to the different categories."
                            className="h-full"
                          >
                            <div className="pt-4 h-80  relative overflow-y-auto">
                              {entityLoading ? (
                                <TableLoader colCount={2} />
                              ) : entityData && entityData?.attackStages ? (
                                <>
                                  {Object.entries(entityData?.attackStages).map(
                                    ([key, value], index) => (
                                      <div
                                        className="w-full text-xs mb-3"
                                        key={index}
                                      >
                                        <div className="w-[70%] inline-block">
                                          {key}
                                        </div>
                                        <div className="inline-block bg-primary text-white px-2 py-1 rounded-sm font-bold">
                                          {value as any}
                                        </div>
                                      </div>
                                    ),
                                  )}
                                </>
                              ) : (
                                <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%]">
                                  <NoData />
                                </div>
                              )}
                            </div>
                          </CustomWidget>
                        </div>
                        <div className="col-span-12">
                          <CustomWidget info="List of anomalies">
                            <DataTableAnomaly
                              data={anomaliesData}
                              cols={anomaliesCol}
                              type="anomaly"
                            />
                          </CustomWidget>
                          {/* <DataTable cols={anomaliesCol} data={anomaliesData} /> */}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="alerts">
                      <CustomWidget title="Alerts" info="List of alerts">
                        <DataTableAnomaly
                          cols={alertsCol}
                          data={alertsData || []}
                          type="alert"
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="outdatedSoftwares">
                      <CustomWidget
                        title="Outdated Softwares"
                        info="List of outdated softwares"
                      >
                        <DataTable
                          cols={outdatedSoftwaresCol}
                          data={outdatedSoftwaresData}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="vulnerabilities">
                      <CustomWidget
                        title="Vulnerabilities"
                        info="List of vulnerabilities"
                      >
                        <DataTable
                          cols={vulnerabilitiesCol}
                          data={vulnerabilitiesData}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>
                    {/* <TabsContent value="externalLogs">
                      <h4 className="font-bold mb-2">Deny Logs</h4>
                      {isLoadingExternalLogs || isFetchingExternalLogs ? (
                        <TableLoader colCount={1} />
                      ) : (
                        <DataTable
                          cols={externalLogsCol}
                          // data={externalLogsData}
                          data={ExternalLogsData}
                          rightOptions={externalLogShadowBtn}
                          noSortColumns={["rawlog"]}
                          countPerPageSelection={true}
                        />
                      )}
                    </TabsContent> */}

                    {/* {isFeatureAllowed(
                      ALL_KEYS.ASSET_ENTITY_INSPECTOR_EXT_LOGS,
                    ) && (
                        <TabsContent value="externalLogs">
                          <h4 className="font-bold mb-2">External Logs</h4>
                          <DataTable
                            cols={externalLogsCol}
                            // data={externalLogsData}
                            data={ExternalLogsData}
                            noSortColumns={["rawlog"]}
                            countPerPageSelection={true}
                          />
                        </TabsContent>
                      )} */}

                    <TabsContent value="userAccess">
                      <CustomWidget
                        title="User Access"
                        info="List of users who have access to this entity"
                      >
                        <DataTable
                          cols={userAccessCol}
                          data={userAccessData || []}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="dhcpSessions">
                      <CustomWidget title="DHCP Sessions">
                        <DataTable
                          cols={dhcpSessionsCol}
                          data={dhcpSessionsData}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="services">
                      <CustomWidget title="Services" info="Services">
                        <DataTable
                          cols={servicesCol}
                          data={
                            entityData?.services
                              ? entityData.services.map((service: string) => {
                                  return { service: service };
                                })
                              : []
                          }
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="inventory">
                      <CustomWidget title="Inventory" info="Inventory">
                        <DataTable
                          cols={inventoryCol}
                          data={inventoryData}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="networkUsers">
                      <CustomWidget title="Network Users" info="Network Users">
                        <DataTable
                          cols={networkUsersCol}
                          data={networkUsersData}
                          countPerPageSelection={true}
                        />
                      </CustomWidget>
                    </TabsContent>

                    <TabsContent value="plugins">
                      <div
                        className="overflow-x-auto"
                        style={{
                          width: `${getFeatureValues(ALL_KEYS.ASSET_ENTITY_INSPECTOR).width || "89vw"}`,
                        }}
                      >
                        <PluginEntityInspector
                          mac={mac}
                          ip={
                            ipAddress ||
                            (entityData ? entityData.ip_addr : "") ||
                            ""
                          }
                          timeFrame={filterTime}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRelatedMacDialog}
        onOpenChange={(status) => setShowRelatedMacDialog(status)}
      >
        <DialogContent className="w-[95vw] sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Related MAC</DialogTitle>
          </DialogHeader>
          <div className="h-64 max-h-[60vh] w-full max-w-full overflow-auto pe-2 [scrollbar-gutter:stable]">
            <div className="min-w-[260px] sm:min-w-[360px] w-full box-border">
              <DataTable
                cols={relatedMacCols}
                data={relatedMacTableData}
                colFilter={false}
                pagination={false}
                countPerPageSelection={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AssetEntityInspector: React.FC<assetEntityInspectortProps> = ({
  label,
  mac,
  onOpen,
  filter_time,
  ipAddress,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => {
    setIsOpen(true);
    onOpen && onOpen();
  };

  return (
    <>
      <Button variant="link" onClick={handleOpen} className="text-xxs">
        {label}
      </Button>
      {isOpen &&
        ReactDOM.createPortal(
          <AssetEntityInspectorContent
            label={label}
            mac={mac}
            onClose={() => setIsOpen(false)}
            filter_time={filter_time}
            ipAddress={ipAddress}
          />,
          document.body,
        )}
    </>
  );
};

export default AssetEntityInspector;
export { AssetEntityInspectorContent };
